  (function () {
    function safeJsonParse(value) {
      try {
        return JSON.parse(value);
      } catch (e) {
        return null;
      }
    }

    // ------------------------------------------------------------------
    // Bottom-sheet staging (mobile): while the sheet is open every filter
    // application is captured instead of rendered, the cookie write is
    // deferred, and param surgery composes on the staged state instead of
    // the address bar. Commit re-plays the staged params through the
    // normal render path; cancel discards them.
    // ------------------------------------------------------------------
    let sheetStagingActive = false;
    let sheetStagedParams = null;
    let sheetStagingNotify = null;
    let sheetDeferredSelection = null;

    function currentParamsBase() {
      return sheetStagingActive && sheetStagedParams !== null
        ? sheetStagedParams
        : window.location.search || '';
    }

    function renderFacetsPage(searchParams, updateURLHash) {
      const sp = String(searchParams || '');
      const shouldUpdate = updateURLHash !== false;

      if (sheetStagingActive) {
        try {
          const staged = new URLSearchParams(sp);
          staged.delete('section_id');
          staged.delete('page');
          sheetStagedParams = staged.toString();
        } catch (e) {
          sheetStagedParams = sp;
        }
        if (sheetStagingNotify) sheetStagingNotify(sheetStagedParams);
        return true;
      }

      // Prevent loops: if the requested params are the same as the current URL params,
      // do nothing.
      try {
        const next = new URLSearchParams(sp);
        const cur = new URLSearchParams(window.location.search || '');
        next.delete('section_id');
        cur.delete('section_id');
        if (next.toString() === cur.toString()) {
          return true;
        }
      } catch (e) {}

      try {
        if (typeof window !== 'undefined' && window.FacetFiltersForm && typeof window.FacetFiltersForm.renderPage === 'function') {
          window.FacetFiltersForm.renderPage(sp, null, shouldUpdate);
          return true;
        }
      } catch (e) {}

      try {
        if (typeof customElements !== 'undefined') {
          const C = customElements.get('facet-filters-form');
          if (C && typeof C.renderPage === 'function') {
            C.renderPage(sp, null, shouldUpdate);
            return true;
          }
        }
      } catch (e) {}

      // Fallback: hard navigation.
      try {
        const params = new URLSearchParams(sp);
        params.delete('section_id');
        window.location.href = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        return true;
      } catch (e) {
        return false;
      }
    }

    function isFitmentFilterDebugEnabled() {
      try {
        const params = new URLSearchParams(window.location.search || '');
        const value = params.get('fitment_filter_debug');
        return value === '1';
      } catch (e) {
        return false;
      }
    }

    if (isFitmentFilterDebugEnabled()) {
      console.log('[Vehicle Fitment] Debug enabled (hrllc-grid-v1.js loaded)');
    }

    function decodeTokenForParsing(token) {
      let t = String(token || '');
      if (t.includes('+')) t = t.replace(/\+/g, ' ');
      if (t.includes('%')) {
        try {
          t = decodeURIComponent(t);
        } catch (e) {}
      }
      return t.trim();
    }

    function twoDigitYearToFourDigit(yy) {
      const n = parseInt(yy, 10);
      if (!Number.isFinite(n)) return null;

      // Heuristic:
      // - Most automotive fitments are 2000+.
      // - If the 2-digit year is "too far" in the future relative to current year, treat as 1900s.
      const currentTwo = new Date().getFullYear() % 100;
      const pivot = currentTwo + 2;
      return n <= pivot ? 2000 + n : 1900 + n;
    }

    function parseYearRangePrefix(prefix) {
      const p = String(prefix || '').trim();
      if (!p) return [];

      const m = p.match(/^(\d{2,4})\s*-\s*(\d{2,4})$/);
      if (!m) return [];

      const startRaw = m[1];
      const endRaw = m[2];

      const start = startRaw.length === 2 ? twoDigitYearToFourDigit(startRaw) : parseInt(startRaw, 10);
      const end = endRaw.length === 2 ? twoDigitYearToFourDigit(endRaw) : parseInt(endRaw, 10);
      if (!Number.isFinite(start) || !Number.isFinite(end)) return [];

      const years = [];
      const step = start <= end ? 1 : -1;
      for (let y = start; step > 0 ? y <= end : y >= end; y += step) years.push(String(y));
      return years;
    }

    function parseRangeLabelToken(rawToken) {
      // Supports labels like:
      // - "12-15 Honda Civic"
      // - "2012-2015 Honda Civic"
      // Returns null if it doesn't match.
      const raw = String(rawToken || '').trim();
      const t = decodeTokenForParsing(raw);
      if (!t) return null;

      const m = t.match(/^(\d{2,4}\s*-\s*\d{2,4})\s+(.+)$/);
      if (!m) return null;

      const years = parseYearRangePrefix(m[1]);
      if (!years.length) return null;

      const rest = String(m[2] || '').trim();
      if (!rest) return null;

      const parts = rest.split(/\s+/).filter(Boolean);
      if (parts.length < 2) return null;

      const make = parts[0];
      const model = parts.slice(1).join(' ');

      // IMPORTANT: source must be the exact token value Shopify expects when applying the filter.
      // That may be URL-encoded and/or contain + signs.
      return { years, make, model, source: raw };
    }

    function parseSingleYearLabelToken(rawToken) {
      // Supports labels like:
      // - "06 Mitsubishi Lancer"
      // - "2006 Mitsubishi Lancer"
      // Returns null if it doesn't match.
      const raw = String(rawToken || '').trim();
      const t = decodeTokenForParsing(raw);
      if (!t) return null;

      const m = t.match(/^(\d{2,4})\s+(.+)$/);
      if (!m) return null;

      const yearRaw = m[1];
      const rest = String(m[2] || '').trim();
      if (!rest) return null;

      const yearNum = yearRaw.length === 2 ? twoDigitYearToFourDigit(yearRaw) : parseInt(yearRaw, 10);
      if (!Number.isFinite(yearNum)) return null;
      const year = String(yearNum);

      const parts = rest.split(/\s+/).filter(Boolean);
      if (parts.length < 2) return null;

      const make = parts[0];
      const model = parts.slice(1).join(' ');

      return { year, make, model, source: raw };
    }

    function parseYearToken(token) {
      const t = String(token || '').trim();
      if (!t) return [];

      // Support: 2022 or 2022-2025
      const rangeMatch = t.match(/^(\d{4})\s*-\s*(\d{4})$/);
      if (rangeMatch) {
        const start = parseInt(rangeMatch[1], 10);
        const end = parseInt(rangeMatch[2], 10);
        if (!Number.isFinite(start) || !Number.isFinite(end)) return [];
        const years = [];
        const step = start <= end ? 1 : -1;
        for (let y = start; step > 0 ? y <= end : y >= end; y += step) years.push(String(y));
        return years;
      }

      const singleMatch = t.match(/^(\d{4})$/);
      if (singleMatch) return [singleMatch[1]];

      return [];
    }

    function normalizeToken(token) {
      return String(token || '').trim();
    }

    function parseFitmentsText(multilineText) {
      const raw = typeof multilineText === 'string' ? multilineText : '';
      const lines = raw
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);

      // Each line: yearOrRange|make|model|trim
      const fitments = [];

      for (const line of lines) {
        const parts = line.split('|').map((p) => p.trim());
        if (parts.length < 3) continue;

        const yearPart = parts[0];
        const make = normalizeToken(parts[1]);
        const model = normalizeToken(parts[2]);
        const trim = normalizeToken(parts[3] || '');

        const years = parseYearToken(yearPart);
        if (!years.length || !make || !model) continue;

        for (const y of years) {
          fitments.push({ year: y, make, model, trim });
        }
      }

      return fitments;
    }

    function parseToken(token) {
      const t = String(token || '').trim();
      if (!t) return null;
      const parts = t.split('|').map((p) => p.trim());
      if (!parts[0]) return null;
      return {
        token: t,
        year: parts[0] || '',
        make: parts[1] || '',
        model: parts[2] || '',
        trim: parts[3] || '',
        depth: parts.length,
      };
    }

    function getFitmentFilterValuesFromDom(paramName) {
      if (!paramName) return [];
      const inputs = document.querySelectorAll(
        '#FacetFiltersForm input[type="checkbox"][name="' + CSS.escape(paramName) + '"]'
      );
      if (!inputs.length) return [];
      return Array.from(inputs).map((input) => String(input.value || '').trim()).filter(Boolean);
    }

    // The generated catalog vehicle list, cached for the session so a shopper
    // moving between search pages pays for it once.
    const CATALOG_TOKEN_CACHE_KEY = 'saiko:fitment-tokens';
    function loadCatalogTokens(url) {
      try {
        const cached = JSON.parse(window.sessionStorage.getItem(CATALOG_TOKEN_CACHE_KEY) || 'null');
        if (cached && cached.url === url && Array.isArray(cached.tokens)) {
          return Promise.resolve(cached.tokens);
        }
      } catch (e) {}
      return fetch(url, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('token asset ' + res.status);
          return res.json();
        })
        .then(function (payload) {
          const tokens = payload && Array.isArray(payload.tokens) ? payload.tokens : [];
          try {
            window.sessionStorage.setItem(
              CATALOG_TOKEN_CACHE_KEY,
              JSON.stringify({ url: url, tokens: tokens, param: (payload && payload.param) || '' })
            );
          } catch (e) {}
          return tokens;
        });
    }

    function getAvailableFitmentValues(container, fitmentParam) {
      // The facet DOM carries the same Search & Discovery value list as the
      // island and is capped at 100 alphabetically truncated values. When the
      // section says its island is the collection's exact vehicle list (from
      // custom.fitment_tokens, or the catalog-wide asset on search), that list
      // wins outright: preferring the facet DOM would put the cap straight
      // back and hide vehicles the collection genuinely stocks.
      if (container.getAttribute('data-tokens-exact') === 'true') {
        const exact = getFitmentTokens(container);
        if (exact.length) return exact;
      }
      const domValues = getFitmentFilterValuesFromDom(fitmentParam);
      if (domValues.length) return domValues;
      return getFitmentTokens(container);
    }

    function logFetchedFitmentValues(container, fitmentParam, tokens) {
      if (!isFitmentFilterDebugEnabled()) return;

      const payload = {
        param: fitmentParam,
        count: Array.isArray(tokens) ? tokens.length : 0,
        sample: Array.isArray(tokens) ? tokens.slice(0, 20) : [],
        tokens,
      };

      try {
        window.__hrllcFitmentTokensDebug = payload;
      } catch (e) {}

      console.log('[Vehicle Fitment] Fitment values fetched', {
        param: payload.param,
        count: payload.count,
        sample: payload.sample,
        windowRef: '__hrllcFitmentTokensDebug',
      });

      if (Array.isArray(tokens) && tokens.length) {
        console.groupCollapsed('[Vehicle Fitment] Fitment values (ALL)');
        tokens.forEach((t) => console.log(t));
        console.groupEnd();
      }
    }

    function logBrandFacetRefetch(container) {
      if (!isFitmentFilterDebugEnabled()) return;
      if (!container) return;

      const brandParam = container.getAttribute('data-brand-filter-param') || '';
      if (!brandParam) return;

      // Source A: the section-embedded collection.filters snapshot
      const filters = getCollectionFiltersDebug(container);
      const brandFilter = Array.isArray(filters)
        ? filters.find((f) => String(f?.param_name || '') === String(brandParam)) ||
          filters.find((f) => String(f?.label || '').toLowerCase() === 'brand') ||
          filters.find((f) => String(f?.param_name || '').includes('vendor'))
        : null;

      const jsonValues = brandFilter && Array.isArray(brandFilter.values) ? brandFilter.values : [];
      const jsonDisabledCount = jsonValues.filter((v) => Number(v?.count) === 0 && v?.active === false).length;
      const jsonActiveCount = jsonValues.filter((v) => v?.active === true).length;

      // Source B: actual facet checkbox DOM (what Search & Discovery uses)
      const selector =
        'input[type="checkbox"][form="FacetsFilterForm"][name="' + CSS.escape(brandParam) + '"]';
      const inputs = document.querySelectorAll(selector);
      let domDisabled = 0;
      let domChecked = 0;
      inputs.forEach((i) => {
        if (i.disabled) domDisabled++;
        if (i.checked) domChecked++;
      });

      const payload = {
        brandParam,
        fromCollectionFiltersJson: {
          found: !!brandFilter,
          label: brandFilter ? brandFilter.label : null,
          param_name: brandFilter ? brandFilter.param_name : null,
          type: brandFilter ? brandFilter.type : null,
          valueCount: jsonValues.length,
          activeCount: jsonActiveCount,
          disabledCount: jsonDisabledCount,
          sample: jsonValues.slice(0, 25),
        },
        fromFacetDom: {
          selector,
          inputCount: inputs.length,
          checkedCount: domChecked,
          disabledCount: domDisabled,
          sample: Array.from(inputs)
            .slice(0, 25)
            .map((i) => ({ value: i.value, checked: i.checked, disabled: i.disabled })),
        },
      };

      try {
        window.__hrllcBrandFacetDebug = payload;
      } catch (e) {}

      console.log('[Filters] Brand (refetch)', {
        brandParam: payload.brandParam,
        json: {
          found: payload.fromCollectionFiltersJson.found,
          param_name: payload.fromCollectionFiltersJson.param_name,
          type: payload.fromCollectionFiltersJson.type,
          valueCount: payload.fromCollectionFiltersJson.valueCount,
          disabledCount: payload.fromCollectionFiltersJson.disabledCount,
          activeCount: payload.fromCollectionFiltersJson.activeCount,
        },
        dom: {
          inputCount: payload.fromFacetDom.inputCount,
          disabledCount: payload.fromFacetDom.disabledCount,
          checkedCount: payload.fromFacetDom.checkedCount,
        },
        windowRef: '__hrllcBrandFacetDebug',
      });
    }

    function getFitmentTokens(container) {
      const sectionId = container.getAttribute('data-section-id');
      const script = document.querySelector(
        '.hrllc-grid-v1-fitment-tokens[data-section-id="' + CSS.escape(sectionId) + '"]'
      );
      if (!script) return [];
      const parsed = safeJsonParse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    }

    function getCollectionFiltersDebug(container) {
      const sectionId = container.getAttribute('data-section-id');
      const script = document.querySelector(
        '.hrllc-grid-v1-collection-filters[data-section-id="' + CSS.escape(sectionId) + '"]'
      );
      if (!script) return [];
      const parsed = safeJsonParse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    }

    function rebuildWheelSelectOptions(selectEl, filter) {
      if (!selectEl) return;
      const values = Array.isArray(filter && filter.values) ? filter.values : [];
      const current = String(selectEl.value || '');
      const placeholder =
        selectEl.options && selectEl.options.length ? String(selectEl.options[0].text || '') : '';

      while (selectEl.firstChild) selectEl.removeChild(selectEl.firstChild);

      const opt0 = document.createElement('option');
      opt0.value = '';
      opt0.textContent = placeholder || '';
      selectEl.appendChild(opt0);

      values.forEach((v) => {
        const opt = document.createElement('option');
        opt.value = v && v.value != null ? String(v.value) : '';
        opt.textContent = v && v.label != null ? String(v.label) : '';
        const isActive = !!(v && v.active);
        const count = Number((v && v.count) || 0);
        if (!isActive && count <= 0) {
          opt.disabled = true;
          opt.hidden = true;
        }
        if (String(opt.value) === current || isActive) {
          opt.selected = true;
        }
        selectEl.appendChild(opt);
      });
    }

    function applyWheelSelectsAjax(selectElements) {
      try {
        const params = new URLSearchParams(window.location.search || '');
        params.delete('page');
        params.delete('section_id');

        (selectElements || []).forEach((sel) => {
          if (!sel) return;
          const name = String(sel.getAttribute('name') || '').trim();
          if (!name) return;
          const value = String(sel.value || '').trim();
          if (!value) params.delete(name);
          else params.set(name, value);
        });

        renderFacetsPage(params.toString(), true);
      } catch (e) {
        try {
          const form = document.getElementById('FacetsFilterForm');
          if (form) form.submit();
        } catch (e2) {}
      }
    }

    function logAllCollectionFilterParams(container) {
      if (!isFitmentFilterDebugEnabled()) return;

      const filters = getCollectionFiltersDebug(container);
      try {
        window.__collectionFiltersDebug = filters;
      } catch (e) {}

      console.log('[Filters] Collection filters', {
        count: filters.length,
        windowRef: '__collectionFiltersDebug',
      });

      try {
        const rows = (Array.isArray(filters) ? filters : []).map((f) => {
          const values = Array.isArray(f?.values) ? f.values : [];
          const activeValues = values.filter((v) => v && v.active === true).map((v) => v.value);
          const sampleValues = values
            .slice(0, 12)
            .map((v) => (v && v.value != null ? String(v.value) : ''))
            .filter(Boolean);

          return {
            label: f?.label,
            param_name: f?.param_name,
            type: f?.type,
            valueCount: values.length,
            activeCount: activeValues.length,
            activeValues: activeValues.slice(0, 8).join(', '),
            sampleValues: sampleValues.join(', '),
          };
        });
        console.table(rows);
      } catch (e) {}

      filters.forEach((f) => {
        const values = Array.isArray(f.values) ? f.values : [];
        console.groupCollapsed('[Filters] ' + String(f.label || ''), {
          param_name: f.param_name,
          type: f.type,
          valueCount: values.length,
        });
        console.log(f);
        values.forEach((v) => console.log(v));
        console.groupEnd();
      });
    }

    function logFacetDomFilters(container) {
      if (!isFitmentFilterDebugEnabled()) return;

      const sectionId = container?.getAttribute ? container.getAttribute('data-section-id') : '';
      const formId = 'FacetsFilterForm';
      const root = sectionId ? document.querySelector('#shopify-section-' + CSS.escape(sectionId)) : null;
      const scope = root || document;
      const selector = 'input[form="' + CSS.escape(formId) + '"][name^="filter."]';
      const inputs = scope.querySelectorAll(selector);

      const byName = new Map();
      inputs.forEach((el) => {
        const name = String(el.getAttribute('name') || '').trim();
        if (!name) return;
        if (!byName.has(name)) byName.set(name, []);
        byName.get(name).push(el);
      });

      const rows = Array.from(byName.entries()).map(([name, els]) => {
        const values = els
          .map((el) => String(el.value || '').trim())
          .filter(Boolean);
        const unique = Array.from(new Set(values));

        const checked = els.filter((el) => el.checked).map((el) => String(el.value || '').trim()).filter(Boolean);
        const disabledCount = els.filter((el) => el.disabled).length;

        return {
          param_name: name,
          valueCount: unique.length,
          inputCount: els.length,
          checkedCount: checked.length,
          disabledCount,
          checkedValues: checked.slice(0, 8).join(', '),
          sampleValues: unique.slice(0, 12).join(', '),
        };
      });

      try {
        window.__facetDomFiltersDebug = rows;
      } catch (e) {}

      console.log('[Filters] Facet DOM filters (Search & Discovery storefront output)', {
        selector,
        groupCount: rows.length,
        windowRef: '__facetDomFiltersDebug',
      });
      try {
        console.table(rows);
      } catch (e) {}
    }

    function getFitmentCodes(container) {
      const sectionId = container.getAttribute('data-section-id');
      const script = document.querySelector(
        '.hrllc-grid-v1-fitment-codes[data-section-id="' + CSS.escape(sectionId) + '"]'
      );
      if (!script) return [];
      const parsed = safeJsonParse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    }

    function buildTokenIndex(tokens, showTrim) {
      const parsed = [];

      // Support two formats:
      // 1) Pipe-delimited values like "2020|Subaru|WRX" (existing)
      // 2) Range-label values like "12-15 Honda Civic" (Shopify Search & Discovery)
      //
      // We expand (2) into per-year entries for populating the dropdowns, but keep a link to
      // the original value so we can apply the correct Shopify filter value.
      for (const t of tokens) {
        const range = parseRangeLabelToken(t);
        if (range) {
          for (const y of range.years) {
            parsed.push({ token: range.source, source: range.source, year: y, make: range.make, model: range.model, trim: '', depth: 3 });
          }
          continue;
        }

        const single = parseSingleYearLabelToken(t);
        if (single) {
          parsed.push({ token: single.source, source: single.source, year: single.year, make: single.make, model: single.model, trim: '', depth: 3 });
          continue;
        }

        const p = parseToken(decodeTokenForParsing(t));
        if (!p) continue;
        // For pipe-delimited tokens, the applied value should be the original token from Shopify.
        parsed.push({ ...p, token: String(t || '').trim(), source: String(t || '').trim() });
      }

      if (isFitmentFilterDebugEnabled()) {
        try {
          window.__hrllcFitmentsExpanded = parsed;
        } catch (e) {}
        console.log('[Vehicle Fitment] Expanded fitments', {
          count: parsed.length,
          sample: parsed.slice(0, 50),
          windowRef: '__hrllcFitmentsExpanded',
        });

        if (parsed.length) {
          console.groupCollapsed('[Vehicle Fitment] Expanded fitments (ALL)');
          parsed.forEach((f) => console.log(f));
          console.groupEnd();
        }
      }

      const years = uniqSorted(
        parsed.map((p) => p.year).filter(Boolean),
        yearComparator
      );

      function makesForYear(year) {
        return uniqSorted(
          parsed
            .filter((p) => p.year === year && p.make)
            .map((p) => p.make)
        );
      }

      function modelsForYearMake(year, make) {
        return uniqSorted(
          parsed
            .filter((p) => p.year === year && p.make === make && p.model)
            .map((p) => p.model)
        );
      }

      function trimsForYearMakeModel(year, make, model) {
        if (!showTrim) return [];
        return uniqSorted(
          parsed
            .filter((p) => p.year === year && p.make === make && p.model === model && p.trim)
            .map((p) => p.trim)
        );
      }

      function filterValueForSelection(selection) {
        // Only apply Shopify filter values when the selection is complete.
        // Returns ALL matching source values: a year/make/model selection can
        // be covered by several stored tokens (per-year tokens, range labels,
        // overlapping ranges from different products), and each is a distinct
        // exact-match filter value that must be applied together.
        if (!selection.year || !selection.make || !selection.model) return [];
        if (showTrim && !selection.trim) return [];

        const matches = parsed.filter((p) => {
          if (p.year !== selection.year) return false;
          if (p.make !== selection.make) return false;
          if (p.model !== selection.model) return false;
          if (showTrim && (p.trim || '') !== (selection.trim || '')) return false;
          return true;
        });

        const values = [];
        for (const match of matches) {
          const value = String(match.source || match.token || '').trim();
          if (value && !values.includes(value)) values.push(value);
        }
        return values;
      }

      return {
        parsed,
        years,
        makesForYear,
        modelsForYearMake,
        trimsForYearMakeModel,
        filterValueForSelection,
        showTrim,
      };
    }

    function selectionToToken(selection, showTrim) {
      // Back-compat helper retained for existing pipe-delimited token format.
      // Note: for range-label filters, we should not apply partial tokens to Shopify.
      if (selection.year && selection.make && selection.model && showTrim && selection.trim) {
        return selection.year + '|' + selection.make + '|' + selection.model + '|' + selection.trim;
      }
      if (selection.year && selection.make && selection.model) {
        return selection.year + '|' + selection.make + '|' + selection.model;
      }
      if (selection.year && selection.make) {
        return selection.year + '|' + selection.make;
      }
      if (selection.year) {
        return selection.year;
      }
      return '';
    }

    function getCurrentUrlParamValue(paramName) {
      if (!paramName) return '';
      const params = new URLSearchParams(window.location.search || '');
      return params.get(paramName) || '';
    }

    function getCurrentUrlParamValues(paramName) {
      if (!paramName) return [];
      const params = new URLSearchParams(window.location.search || '');
      return params.getAll(paramName).map((v) => String(v || '').trim()).filter(Boolean);
    }

    function applyShopifyFitmentFilter(container, filterValue, skipRender, allowRoute) {
      const paramName = container.getAttribute('data-fitment-filter-param') || '';
      if (!paramName) return;

      // On a surface the fitment index does not cover (/collections/all
      // returns its full catalog whatever value is applied), filtering in
      // place is a lie. Hand the vehicle to search, which does facet fitment
      // storewide. Same routing, and same reason, as the home hero.
      //
      // Only ever on an explicit pick: the cookie prefill also applies a
      // vehicle on load, and navigating away from the page the shopper asked
      // for would be a hijack, not a feature.
      if (container.getAttribute('data-fitment-route') === 'search') {
        // Never write the param here even when not routing: it cannot filter,
        // so a cookie prefill would leave the bar claiming a vehicle over an
        // unfiltered grid. The segments still show the saved vehicle.
        if (!allowRoute) return;
        const routed = (Array.isArray(filterValue) ? filterValue : [filterValue])
          .map((v) => String(v || '').trim())
          .filter(Boolean);
        if (!routed.length || skipRender) return;
        const selection = readSelectionFromUI(container);
        const terms = [selection.make, selection.model].filter(Boolean).join(' ');
        const params = routed.map(function (v) {
          return encodeURIComponent(paramName) + '=' + encodeURIComponent(v);
        });
        window.location.assign('/search?q=' + encodeURIComponent(terms) + '&' + params.join('&'));
        return;
      }

      const input = document.querySelector('.hrllc-grid-v1-fitment-input[name="' + CSS.escape(paramName) + '"]');
      if (!input) return;

      // Accept a single value or an array. A vehicle selection can match
      // several stored tokens (per-year and range-label formats, or
      // overlapping ranges across products); Shopify ORs repeated filter
      // params, so every matching value must be applied together.
      const values = (Array.isArray(filterValue) ? filterValue : [filterValue])
        .map((v) => String(v || '').trim())
        .filter(Boolean);

      if (!values.length) {
        input.value = '';
        input.setAttribute('disabled', 'disabled');
      } else {
        input.value = values[0];
        input.removeAttribute('disabled');
      }

      const form = document.getElementById('FacetsFilterForm');
      if (!form) return;

      // Manage extra hidden inputs (same name) for additional values.
      form
        .querySelectorAll('.hrllc-grid-v1-fitment-input-extra')
        .forEach((extra) => extra.remove());
      values.slice(1).forEach((value) => {
        const extra = document.createElement('input');
        extra.type = 'hidden';
        extra.name = paramName;
        extra.value = value;
        extra.className = 'hrllc-grid-v1-fitment-input-extra';
        form.appendChild(extra);
      });

      if (skipRender) return;

      // IMPORTANT: FormData excludes disabled inputs, so building the URL
      // from the form silently drops filters whose hidden inputs are
      // disabled (brand, fitment codes). Build from the current URL (or
      // the staged sheet state) and touch only the fitment params
      // (repeated values OR together).
      const params = new URLSearchParams(currentParamsBase());
      params.delete(paramName);
      values.forEach((value) => params.append(paramName, value));
      params.delete('page');
      renderFacetsPage(params.toString(), true);
    }

    function applyShopifyBrandFilter(container, brandValue) {
      const paramName = container.getAttribute('data-brand-filter-param') || '';
      if (!paramName) return;

      const input = document.querySelector(
        '.hrllc-grid-v1-brand-input[name="' + CSS.escape(paramName) + '"]'
      );
      if (!input) return;

      const brand = String(brandValue || '').trim();
      if (!brand) {
        input.value = '';
        input.setAttribute('disabled', 'disabled');
      } else {
        input.value = brand;
        input.removeAttribute('disabled');
      }

      const form = document.getElementById('FacetsFilterForm');
      if (!form) return;

      // IMPORTANT: FormData excludes disabled inputs, so building the URL
      // from the form silently drops filters whose hidden inputs are
      // disabled (the applied vehicle, categories). Build from the current
      // URL (or the staged sheet state) and surgically set/delete only
      // the brand param.
      const params = new URLSearchParams(currentParamsBase());
      if (brand) params.set(paramName, brand);
      else params.delete(paramName);
      params.delete('page');
      renderFacetsPage(params.toString(), true);
    }

    function applyShopifyFitmentCodeFilter(container, fitmentCode) {
      const paramName = container.getAttribute('data-fitment-filter-param') || '';
      if (!paramName) return;

      const input = document.querySelector(
        '.hrllc-grid-v1-fitment-code-input[name="' + CSS.escape(paramName) + '"]'
      );
      if (!input) return;

      const code = String(fitmentCode || '').trim();
      if (!code) {
        input.value = '';
        input.setAttribute('disabled', 'disabled');
      } else {
        input.value = code;
        input.removeAttribute('disabled');
      }

      const form = document.getElementById('FacetsFilterForm');
      if (!form) return;

      // Same FormData-excludes-disabled trap as the brand path: rebuild
      // from the current URL (or staged sheet state) and touch only this
      // param.
      const params = new URLSearchParams(currentParamsBase());
      if (code) params.set(paramName, code);
      else params.delete(paramName);
      params.delete('page');
      renderFacetsPage(params.toString(), true);
    }

    function setCookie(name, value, days) {
      const d = new Date();
      d.setTime(d.getTime() + (Number(days) || 30) * 24 * 60 * 60 * 1000);
      const expires = 'expires=' + d.toUTCString();
      document.cookie =
        encodeURIComponent(name) +
        '=' +
        encodeURIComponent(value) +
        ';' +
        expires +
        ';path=/;SameSite=Lax';
    }

    function getCookie(name) {
      const n = encodeURIComponent(name) + '=';
      const decoded = decodeURIComponent(document.cookie || '');
      const parts = decoded.split(';');
      for (let i = 0; i < parts.length; i++) {
        let c = parts[i].trim();
        if (c.indexOf(n) === 0) return c.substring(n.length, c.length);
      }
      return '';
    }

    function deleteCookie(name) {
      document.cookie = encodeURIComponent(name) + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax';
    }

    function normalizePerPage(value) {
      const n = parseInt(String(value || ''), 10);
      if (n === 8 || n === 12 || n === 16 || n === 32) return n;
      return null;
    }

    function getPerPageFromUrl() {
      try {
        const params = new URLSearchParams(window.location.search || '');
        return normalizePerPage(params.get('per_page'));
      } catch (e) {
        return null;
      }
    }

    function bindPerPageDropdown(root) {
      const scope = root || document;
      const perPageSelectEl = scope.querySelector('#collection-per-page');
      if (!perPageSelectEl) return;

      if (perPageSelectEl.__hrllcBound === true) return;
      perPageSelectEl.__hrllcBound = true;

      const cookieName = 'hrllc_per_page';
      const defaultValue = normalizePerPage(perPageSelectEl.getAttribute('data-default')) || 16;

      const urlValue = getPerPageFromUrl();
      const cookieValue = normalizePerPage(getCookie(cookieName));
      const desired = urlValue || cookieValue || defaultValue;

      perPageSelectEl.value = String(desired);

      // Apply on initial bind (covers first page load and section refresh).
      applyClientSidePerPage(scope);

      perPageSelectEl.addEventListener('change', function () {
        const next = normalizePerPage(perPageSelectEl.value) || defaultValue;
        perPageSelectEl.value = String(next);
        setCookie(cookieName, String(next), 365);

        // Client-side: just show/hide already-rendered items.
        applyClientSidePerPage(scope);

        try {
          const params = new URLSearchParams(window.location.search || '');
          params.set('per_page', String(next));
          params.delete('page');
          params.delete('section_id');
          window.location.href = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        } catch (e) {
          window.location.href = window.location.pathname + '?per_page=' + encodeURIComponent(String(next));
        }
      });
    }

    // The instrument's Clear all reloads the bare page: filters go, but
    // the page's identity survives. On collections that is just the
    // sort preference; on the search template the term (and its prefix
    // option and type) IS the page, and dropping it would strand the
    // shopper on an empty search form.
    function bareUrlPreservingIdentity() {
      const params = new URLSearchParams(window.location.search || '');
      const keep = new URLSearchParams();
      ['q', 'options[prefix]', 'type', 'sort_by'].forEach(function (key) {
        const value = params.get(key);
        if (value !== null && value !== '') keep.set(key, value);
      });
      return window.location.pathname + (keep.toString() ? '?' + keep.toString() : '');
    }

    function setSelectOptions(selectEl, values, placeholder) {
      const current = selectEl.value;
      selectEl.innerHTML = '';

      const opt0 = document.createElement('option');
      opt0.value = '';
      opt0.textContent = placeholder || 'Any';
      selectEl.appendChild(opt0);

      // Accepts plain strings or {value, label} pairs: Shopify's vendor
      // filter emits HTML-escaped param values ("K&amp;N") with clean
      // labels, so the two must travel separately.
      const optionValues = [];
      values.forEach((v) => {
        const opt = document.createElement('option');
        if (v && typeof v === 'object') {
          opt.value = v.value;
          opt.textContent = v.label || v.value;
        } else {
          opt.value = v;
          opt.textContent = v;
        }
        optionValues.push(opt.value);
        selectEl.appendChild(opt);
      });

      // Restore if still present
      if (current && optionValues.includes(current)) selectEl.value = current;
    }

    function uniqSorted(arr, comparator) {
      const seen = new Set();
      const out = [];
      for (const v of arr) {
        const key = String(v);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(v);
      }
      if (comparator) out.sort(comparator);
      else out.sort();
      return out;
    }

    function yearComparator(a, b) {
      return parseInt(a, 10) - parseInt(b, 10);
    }

    function applyClientSidePerPage(root) {
      const scope = root || document;
      const perPageSelectEl = scope.querySelector('#collection-per-page');
      if (!perPageSelectEl) return;

      const next = normalizePerPage(perPageSelectEl.value);
      if (!next) return;

      const grid = scope.querySelector('#product-grid');
      if (!grid) return;

      // Only count actual product cards, not inserted blocks.
      const productItems = Array.from(grid.querySelectorAll('li.grid-item:not(.grid-item-block)'));
      productItems.forEach((li, idx) => {
        li.style.display = idx < next ? '' : 'none';
      });
    }

    async function fetchAllCollectionProductsHandles() {
      // Fallback: best-effort; we will not rely on it unless index_all_pages is enabled.
      // Shopify has a built-in /collections/{handle}/products.json endpoint.
      // This is limited by rate and may not include metafields. We'll use it only to get handles and then use the section DOM.
      return [];
    }

    function mergeFitmentsUnique(existing, additions) {
      const out = [];
      const seen = new Set();

      for (const f of existing) {
        const key = [f.year, f.make, f.model, f.trim].join('|');
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(f);
      }

      for (const f of additions) {
        const key = [f.year, f.make, f.model, f.trim].join('|');
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(f);
      }

      return out;
    }

    function getSearchParamsExcluding(keys) {
      const params = new URLSearchParams(window.location.search || '');
      keys.forEach((k) => params.delete(k));
      const str = params.toString();
      return str;
    }

    function getMaxPageFromPagination(root) {
      try {
        const container = root && root.querySelector ? root.querySelector('#ProductGridContainer') : null;
        const scope = container || root || document;
        const links = Array.from(scope.querySelectorAll('a[href*="page="]'));
        let max = 1;
        for (const a of links) {
          const href = a.getAttribute('href') || '';
          if (!href) continue;
          let page = null;
          try {
            const u = new URL(href, window.location.origin);
            const p = u.searchParams.get('page');
            page = p ? parseInt(p, 10) : null;
          } catch (e) {
            const m = href.match(/[?&]page=(\d+)/);
            page = m ? parseInt(m[1], 10) : null;
          }
          if (Number.isFinite(page) && page > max) max = page;
        }
        return max;
      } catch (e) {
        return 1;
      }
    }

    async function fetchSectionPageVendors(sectionId, page, extraParamsString) {
      const extra = String(extraParamsString || '').trim();
      const url =
        window.location.pathname +
        '?section_id=' +
        encodeURIComponent(sectionId) +
        '&page=' +
        encodeURIComponent(String(page)) +
        (extra ? '&' + extra : '');

      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return [];

      const html = await res.text();
      const parsedDom = new DOMParser().parseFromString(html, 'text/html');
      const pageSection = parsedDom.getElementById('shopify-section-' + sectionId) || parsedDom;

      const items = Array.from(pageSection.querySelectorAll('#product-grid > li.grid-item'));
      const vendors = [];
      const seen = new Set();
      for (const li of items) {
        if (li.classList.contains('grid-item-block')) continue;
        const vendor = String(li.getAttribute('data-hrllc-vendor') || '').trim();
        if (!vendor) continue;
        if (seen.has(vendor)) continue;
        seen.add(vendor);
        vendors.push(vendor);
      }

      vendors.sort();
      return vendors;
    }

    async function fetchSectionPageFitments(sectionId, page, showTrim) {
      const extra = getSearchParamsExcluding(['page', 'section_id']);
      const url =
        window.location.pathname +
        '?section_id=' +
        encodeURIComponent(sectionId) +
        '&page=' +
        encodeURIComponent(String(page)) +
        (extra ? '&' + extra : '');

      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return [];

      const html = await res.text();
      const parsedDom = new DOMParser().parseFromString(html, 'text/html');
      const pageSection = parsedDom.getElementById('shopify-section-' + sectionId) || parsedDom;

      const items = Array.from(pageSection.querySelectorAll('#product-grid > li.grid-item'));
      const fitments = [];

      for (const li of items) {
        const data = li.getAttribute('data-hrllc-fitments');
        if (!data) continue;
        const parsed = safeJsonParse(data);
        const fitmentsText = Array.isArray(parsed)
          ? parsed.join('\n')
          : (typeof parsed === 'string' ? parsed : '');
        fitments.push(...parseFitmentsText(fitmentsText));
      }

      // showTrim affects filtering, but fitment parsing doesn't require it.
      // We keep showTrim here for future extension.
      void showTrim;

      return fitments;
    }

    function buildDatasetFromGridItems(root, showTrim) {
      const items = Array.from(root.querySelectorAll('#product-grid > li.grid-item'));
      const productFitmentsMap = new Map(); // liEl => fitments array
      const allFitments = [];

      for (const li of items) {
        const data = li.getAttribute('data-hrllc-fitments');
        if (!data) continue;

        const parsed = safeJsonParse(data);
        // parsed is either string (the metafield text) or null
        const fitmentsText = Array.isArray(parsed)
          ? parsed.join('\n')
          : (typeof parsed === 'string' ? parsed : '');
        const fitments = parseFitmentsText(fitmentsText);
        productFitmentsMap.set(li, fitments);
        allFitments.push(...fitments);
      }

      const years = uniqSorted(
        allFitments.map((f) => f.year).filter(Boolean),
        yearComparator
      );
      const makes = uniqSorted(allFitments.map((f) => f.make).filter(Boolean));
      const models = uniqSorted(allFitments.map((f) => f.model).filter(Boolean));
      const trims = uniqSorted(allFitments.map((f) => f.trim).filter(Boolean));

      return {
        items,
        productFitmentsMap,
        allFitments,
        years,
        makes,
        models,
        trims,
        showTrim,
      };
    }

    function filterPossibleValues(dataset, selection) {
      const { allFitments, showTrim } = dataset;

      const filtered = allFitments.filter((f) => {
        if (selection.year && f.year !== selection.year) return false;
        if (selection.make && f.make !== selection.make) return false;
        if (selection.model && f.model !== selection.model) return false;
        if (showTrim && selection.trim && f.trim !== selection.trim) return false;
        return true;
      });

      return {
        years: uniqSorted(filtered.map((f) => f.year).filter(Boolean), yearComparator),
        makes: uniqSorted(filtered.map((f) => f.make).filter(Boolean)),
        models: uniqSorted(filtered.map((f) => f.model).filter(Boolean)),
        trims: uniqSorted(filtered.map((f) => f.trim).filter(Boolean)),
      };
    }

    function applyProductFiltering(dataset, selection) {
      const { productFitmentsMap, showTrim } = dataset;

      let visibleCount = 0;

      for (const [li, fitments] of productFitmentsMap.entries()) {
        const matches = fitments.some((f) => {
          if (selection.year && f.year !== selection.year) return false;
          if (selection.make && f.make !== selection.make) return false;
          if (selection.model && f.model !== selection.model) return false;
          if (showTrim && selection.trim && f.trim !== selection.trim) return false;
          return true;
        });

        const shouldShow = !selection.year && !selection.make && !selection.model && (!showTrim || !selection.trim)
          ? true
          : matches;

        li.style.display = shouldShow ? '' : 'none';
        if (shouldShow) visibleCount++;
      }

      return visibleCount;
    }

    function buildFitmentCodeIndex(root) {
      const items = Array.from(root.querySelectorAll('#product-grid > li.grid-item'));
      const codes = uniqSorted(
        items
          .map((li) => (li.getAttribute('data-hrllc-fitment-code') || '').trim())
          .filter(Boolean)
      );
      return { items, codes };
    }

    function applyFitmentCodeFiltering(root, selectedCode) {
      const code = (selectedCode || '').trim();
      const items = Array.from(root.querySelectorAll('#product-grid > li.grid-item'));

      for (const li of items) {
        if (li.classList.contains('grid-item-block')) {
          li.style.display = '';
          continue;
        }

        if (!code) {
          li.style.display = '';
          continue;
        }

        const liCode = (li.getAttribute('data-hrllc-fitment-code') || '').trim();
        li.style.display = liCode && liCode === code ? '' : 'none';
      }
    }

    function readSelectionFromUI(container) {
      const year = container.querySelector('.hrllc-grid-v1-year')?.value || '';
      const make = container.querySelector('.hrllc-grid-v1-make')?.value || '';
      const model = container.querySelector('.hrllc-grid-v1-model')?.value || '';
      const trim = container.querySelector('.hrllc-grid-v1-trim')?.value || '';

      const fitment_code = container.querySelector('.hrllc-grid-v1-fitment-code')?.value || '';

      return { year, make, model, trim, fitment_code };
    }

    function setSelectionToUI(container, selection, showTrim) {
      const yearEl = container.querySelector('.hrllc-grid-v1-year');
      const makeEl = container.querySelector('.hrllc-grid-v1-make');
      const modelEl = container.querySelector('.hrllc-grid-v1-model');
      const trimEl = container.querySelector('.hrllc-grid-v1-trim');
      const fitmentCodeEl = container.querySelector('.hrllc-grid-v1-fitment-code');

      if (yearEl) yearEl.value = selection.year || '';
      if (makeEl) makeEl.value = selection.make || '';
      if (modelEl) modelEl.value = selection.model || '';
      if (showTrim && trimEl) trimEl.value = selection.trim || '';
      if (fitmentCodeEl) fitmentCodeEl.value = selection.fitment_code || '';
    }

    // The shopper's own wheel spec, stated on the wheels surfaces where the
    // grid is organized by spec and nothing else knows what car they drive.
    // Data comes from snippets/saikospeed-vehicle-specs.liquid, generated from
    // the ratified table in tools/fitment-code-sync/vehicle-specs.json.
    //
    // The rules the table enforces are the rules here too: exact make, exact
    // model, year inside the row's range, and no answer at all when there is
    // no row. Bolt pattern and thread pitch are the two specs where being
    // wrong is a wheel coming off a car, so a near miss is worse than silence.
    let vehicleSpecData = null;

    function getVehicleSpecData() {
      if (vehicleSpecData) return vehicleSpecData;
      const island = document.querySelector('[data-saikospeed-vehicle-specs]');
      const parsed = island ? safeJsonParse(island.textContent || '') : null;
      vehicleSpecData = {
        rows: parsed && Array.isArray(parsed.rows) ? parsed.rows : [],
        routes: parsed && parsed.routes ? parsed.routes : {},
      };
      return vehicleSpecData;
    }

    function getVehicleSpecRows() {
      return getVehicleSpecData().rows;
    }

    // Which bolt pattern this page is selling. Taken from the collection title
    // and only believed if some vehicle in the ratified table actually takes
    // it, so "Stage Wheels" or a renamed collection quietly means no claim
    // rather than a wrong one.
    function surfacePattern(container) {
      const title = String(container.getAttribute('data-surface-title') || '').trim().toLowerCase();
      if (!title) return '';
      const rows = getVehicleSpecRows();
      for (let i = 0; i < rows.length; i += 1) {
        if (rows[i].p && rows[i].p.toLowerCase() === title) return rows[i].p;
      }
      return '';
    }

    function resolveVehicleSpec(selection) {
      const year = parseInt(String(selection && selection.year), 10);
      const make = String((selection && selection.make) || '').trim();
      const model = String((selection && selection.model) || '').trim();
      if (!year || !make || !model) return null;
      const rows = getVehicleSpecRows();
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        if (row.mk === make && row.md === model && year >= row.a && year <= row.b) return row;
      }
      return null;
    }

    // "Your 2020 Honda Civic Si takes 5x114.3 wheels, M12x1.5 lug nuts." The
    // wording states the VEHICLE's spec rather than promising that anything on
    // the page is verified for that car, which is a claim this data cannot
    // support and the fitment guarantee should not have to cover.
    function updateVehicleSpecLine(container) {
      const line = container.querySelector('[data-saikospeed-vehicle-spec]');
      if (!line) return;
      const selection = loadSelection(container);
      const spec = resolveVehicleSpec(selection);
      while (line.firstChild) line.removeChild(line.firstChild);
      line.classList.remove('saikospeed-vehicle-spec--mismatch');
      if (!spec || (!spec.p && !spec.t)) {
        line.hidden = true;
        return;
      }

      // A shopper standing in the wrong bolt pattern is the case this whole
      // table exists for, so say it plainly and hand them the way out. The
      // claim is only made when BOTH patterns are known: a vehicle whose row
      // states no pattern gets the neutral sentence, never a warning it
      // cannot back up.
      // On a fastener surface the bolt pattern is not what they are buying, so
      // the sentence is trimmed to the thread. A lug nut in the wrong thread is
      // the mistake this page can actually cause.
      const fastenerSurface = container.getAttribute('data-spec-emphasis') === 'fastener';
      if (fastenerSurface && !spec.t) {
        line.hidden = true;
        return;
      }

      const surface = fastenerSurface ? '' : surfacePattern(container);
      const mismatched = !!(surface && spec.p && surface !== spec.p);
      if (mismatched) {
        line.classList.add('saikospeed-vehicle-spec--mismatch');
        line.appendChild(document.createTextNode('These are '));
        const theirs = document.createElement('strong');
        theirs.textContent = surface;
        line.appendChild(theirs);
        line.appendChild(document.createTextNode(' wheels. '));
      }

      const vehicle = [selection.year, selection.make, selection.model].join(' ');
      line.appendChild(document.createTextNode('Your '));
      const car = document.createElement('strong');
      car.textContent = vehicle;
      line.appendChild(car);
      line.appendChild(document.createTextNode(' takes '));

      const parts = [];
      if (spec.p && !fastenerSurface) parts.push(spec.p + ' wheels');
      if (spec.t) parts.push(spec.t + (spec.f === 'bolt' ? ' wheel bolts' : ' lug nuts'));
      parts.forEach(function (text, index) {
        if (index) line.appendChild(document.createTextNode(', '));
        const strong = document.createElement('strong');
        strong.textContent = text;
        line.appendChild(strong);
      });
      // The hub is a detail worth having when the page already fits; in the
      // corrective sentence it only blunts the point, and on a fastener page
      // it is beside the point entirely.
      if (spec.h && !mismatched && !fastenerSurface) {
        line.appendChild(document.createTextNode(' on a ' + spec.h + 'mm hub'));
      }
      line.appendChild(document.createTextNode('.'));

      const route = mismatched ? getVehicleSpecData().routes[spec.p] : '';
      if (route) {
        const link = document.createElement('a');
        link.className = 'saikospeed-vehicle-spec__route';
        link.href = (window.routes && window.routes.root_url ? String(window.routes.root_url).replace(/\/$/, '') : '') + '/collections/' + route;
        link.textContent = 'Show ' + spec.p + ' wheels';
        line.appendChild(document.createTextNode(' '));
        line.appendChild(link);
      }
      line.hidden = false;
    }

    // The tool band's identity line ("Shopping for your 2018 Honda Civic
    // Si"). Server-rendered from the active filter on load; kept current
    // here as the shopper selects or clears. A partial selection announces
    // nothing rather than half a vehicle.
    function updateVehicleStatus(selection) {
      const status = document.querySelector('[data-hrllc-vehicle-status]');
      if (!status) return;
      const parts = [selection.year, selection.make, selection.model]
        .map((part) => String(part || '').trim())
        .filter(Boolean);
      const chassisLabel = status.getAttribute('data-chassis-label') || '';
      while (status.firstChild) status.removeChild(status.firstChild);
      if (parts.length === 3) {
        status.appendChild(document.createTextNode('Shopping for your '));
        const strong = document.createElement('strong');
        strong.textContent = parts.join(' ');
        status.appendChild(strong);
        status.hidden = false;
      } else if (chassisLabel) {
        // On a chassis collection the identity never goes blank; it falls
        // back to the chassis itself.
        status.appendChild(document.createTextNode('Shopping for '));
        const strong = document.createElement('strong');
        strong.textContent = chassisLabel;
        status.appendChild(strong);
        status.hidden = false;
      } else {
        status.hidden = true;
      }
    }

    function persistSelection(container, selection) {
      if (sheetStagingActive) {
        // Nothing durable happens while the sheet stages: the cookie
        // writes on commit, or never (cancel).
        sheetDeferredSelection = { container: container, selection: selection };
        return;
      }
      const cookieName = container.getAttribute('data-cookie-name') || 'hrllc_vehicle';
      const cookieDays = parseInt(container.getAttribute('data-cookie-days') || '30', 10);

      const isEmpty =
        !selection.year &&
        !selection.make &&
        !selection.model &&
        !selection.trim &&
        !selection.fitment_code;

      if (isEmpty) {
        deleteCookie(cookieName);
        updateVehicleStatus(selection);
        return;
      }

      setCookie(cookieName, JSON.stringify(selection), cookieDays);
      updateVehicleStatus(selection);
    }

    function loadSelection(container) {
      const cookieName = container.getAttribute('data-cookie-name') || 'hrllc_vehicle';
      const raw = getCookie(cookieName);
      if (!raw) return { year: '', make: '', model: '', trim: '', fitment_code: '' };

      const parsed = safeJsonParse(raw);
      if (!parsed || typeof parsed !== 'object') return { year: '', make: '', model: '', trim: '', fitment_code: '' };

      return {
        year: typeof parsed.year === 'string' ? parsed.year : '',
        make: typeof parsed.make === 'string' ? parsed.make : '',
        model: typeof parsed.model === 'string' ? parsed.model : '',
        trim: typeof parsed.trim === 'string' ? parsed.trim : '',
        fitment_code: typeof parsed.fitment_code === 'string' ? parsed.fitment_code : '',
      };
    }

    function clearSelection(container, showTrim) {
      const cookieName = container.getAttribute('data-cookie-name') || 'hrllc_vehicle';
      deleteCookie(cookieName);
      updateVehicleStatus({ year: '', make: '', model: '' });

      const yearEl = container.querySelector('.hrllc-grid-v1-year');
      const makeEl = container.querySelector('.hrllc-grid-v1-make');
      const modelEl = container.querySelector('.hrllc-grid-v1-model');
      const trimEl = container.querySelector('.hrllc-grid-v1-trim');
      const fitmentCodeEl = container.querySelector('.hrllc-grid-v1-fitment-code');

      if (yearEl) yearEl.value = '';
      if (makeEl) makeEl.value = '';
      if (modelEl) modelEl.value = '';
      if (showTrim && trimEl) trimEl.value = '';
      if (fitmentCodeEl) fitmentCodeEl.value = '';
      container.dispatchEvent(new CustomEvent('saikospeed:instrument:sync'));
    }

    function updateEnabledStates(container, showTrim) {
      const selection = readSelectionFromUI(container);
      const makeEl = container.querySelector('.hrllc-grid-v1-make');
      const modelEl = container.querySelector('.hrllc-grid-v1-model');
      const trimEl = container.querySelector('.hrllc-grid-v1-trim');

      if (makeEl) makeEl.disabled = !selection.year;
      if (modelEl) modelEl.disabled = !selection.year || !selection.make;
      if (showTrim && trimEl) trimEl.disabled = !selection.year || !selection.make || !selection.model;
    }

    // ------------------------------------------------------------------
    // Instrument bar (chassis pages): each segment is a micro-label over
    // a facade value with the real select riding invisibly on top. Fine
    // pointers get a branded dropdown panel built from the select's
    // options at open time (so it is always current); touch keeps the
    // native picker, and keyboard users operate the select natively.
    // ------------------------------------------------------------------
    function syncInstrumentSegment(seg, select) {
      const valueEl = seg.querySelector('[data-instrument-value]');
      if (!valueEl) return;
      const allLabel = seg.getAttribute('data-all-label') || 'All';
      const opt = select.selectedOptions && select.selectedOptions[0];
      const raw = opt ? opt.textContent.trim() : '';
      const m = raw.match(/^(.*?)(\s*\((\d+)\))?$/);
      while (valueEl.firstChild) valueEl.removeChild(valueEl.firstChild);
      if (select.value) {
        valueEl.appendChild(document.createTextNode(m ? m[1] : raw));
        if (m && m[3]) {
          const count = document.createElement('span');
          count.className = 'saikospeed-instrument__count';
          count.textContent = ' ' + m[3];
          valueEl.appendChild(count);
        }
        seg.classList.add('is-set');
      } else {
        valueEl.textContent = allLabel;
        seg.classList.remove('is-set');
      }
      seg.classList.toggle('is-disabled', select.disabled);
    }

    function buildInstrumentPanel(seg, select, panel) {
      while (panel.firstChild) panel.removeChild(panel.firstChild);
      const allLabel = seg.getAttribute('data-all-label') || 'All';
      let currentBtn = null;
      Array.prototype.forEach.call(select.options, function (opt) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'saikospeed-instrument__option';
        btn.setAttribute('role', 'option');
        const isCurrent = opt.value === select.value;
        btn.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
        if (isCurrent && opt.value) currentBtn = btn;
        const raw = opt.textContent.trim();
        const m = raw.match(/^(.*?)(\s*\((\d+)\))?$/);
        const name = document.createElement('span');
        name.textContent = opt.value ? (m ? m[1] : raw) : allLabel;
        btn.appendChild(name);
        if (m && m[3]) {
          const count = document.createElement('span');
          count.className = 'saikospeed-instrument__option-count';
          count.textContent = m[3];
          btn.appendChild(count);
        }
        btn.addEventListener('click', function (event) {
          event.stopPropagation();
          closeInstrumentPanels();
          if (opt.value !== select.value) {
            select.value = opt.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        panel.appendChild(btn);
      });

      // Long option lists (30 model years, 60 product types) scroll: open on
      // the current value instead of at the top with it out of sight. Direct
      // scrollTop, never scrollIntoView, which would also scroll the page.
      panel.scrollTop = currentBtn
        ? Math.max(0, currentBtn.offsetTop - (panel.clientHeight - currentBtn.offsetHeight) / 2)
        : 0;
    }

    function closeInstrumentPanels() {
      document.querySelectorAll('.saikospeed-instrument__seg.is-open').forEach(function (seg) {
        seg.classList.remove('is-open');
      });
    }

    function parseOffsetNumber(value) {
      if (value === undefined || value === null || value === '') return null;
      const n = parseFloat(String(value).replace('+', ''));
      return isNaN(n) ? null : n;
    }

    // Offsets are signed numbers stored as strings ("+35", "0", "-12");
    // lexical option order reads scrambled, so the pair's panels and
    // native pickers sort numerically.
    function sortOffsetOptions(sel) {
      if (!sel || sel.options.length < 3) return;
      const current = sel.value;
      const opts = Array.prototype.slice.call(sel.options, 1);
      opts.sort(function (a, b) {
        const an = parseOffsetNumber(a.value);
        const bn = parseOffsetNumber(b.value);
        return (an === null ? 0 : an) - (bn === null ? 0 : bn);
      });
      opts.forEach(function (o) {
        sel.appendChild(o);
      });
      sel.value = current;
    }

    // Rebuild the wheels spec selects (singles by name, the offset pair by
    // its data-offset-param) from a collection.filters array: fresh scoped
    // counts, zero-count values pruned, numeric offset order.
    function refreshWheelSelectsFrom(root, container, filters) {
      const singles = root.querySelectorAll('.saikospeed-wheel-select');
      const minSel = root.querySelector('.saikospeed-wheel-offset-min');
      const maxSel = root.querySelector('.saikospeed-wheel-offset-max');
      if (!singles.length && !minSel && !maxSel) return;
      if (!Array.isArray(filters) || !filters.length) return;

      const findByParam = function (p) {
        return filters.find(function (f) {
          return String((f && f.param_name) || '') === p;
        });
      };

      const fill = function (selectEl, filter, selectedValue) {
        const placeholder =
          selectEl.options && selectEl.options.length ? String(selectEl.options[0].text || '') : 'All';
        while (selectEl.firstChild) selectEl.removeChild(selectEl.firstChild);
        const opt0 = document.createElement('option');
        opt0.value = '';
        opt0.textContent = placeholder;
        selectEl.appendChild(opt0);
        filter.values.forEach(function (v) {
          if (!v) return;
          const isActive = !!v.active;
          const count = Number(v.count || 0);
          if (!isActive && count <= 0) return;
          const opt = document.createElement('option');
          opt.value = v.value != null ? String(v.value) : '';
          opt.textContent = String(v.label != null ? v.label : v.value) + ' (' + count + ')';
          if (String(opt.value) === String(selectedValue || '')) opt.selected = true;
          selectEl.appendChild(opt);
        });
        if (!selectEl.selectedOptions.length || !selectEl.value) selectEl.value = String(selectedValue || '');
      };

      Array.prototype.forEach.call(singles, function (sel) {
        const filter = findByParam(sel.getAttribute('name') || '');
        if (!filter || !Array.isArray(filter.values)) return;
        const active = filter.values.find(function (v) {
          return v && v.active;
        });
        fill(sel, filter, active ? active.value : sel.value);
      });

      if (minSel || maxSel) {
        const param =
          (minSel && minSel.getAttribute('data-offset-param')) ||
          (maxSel && maxSel.getAttribute('data-offset-param')) ||
          '';
        const filter = param ? findByParam(param) : null;
        if (filter && Array.isArray(filter.values)) {
          let minValue = '';
          let maxValue = '';
          let minNum = null;
          let maxNum = null;
          filter.values.forEach(function (v) {
            if (!v || !v.active) return;
            const n = parseOffsetNumber(v.value);
            if (n === null) return;
            if (minNum === null || n < minNum) {
              minNum = n;
              minValue = String(v.value);
            }
            if (maxNum === null || n > maxNum) {
              maxNum = n;
              maxValue = String(v.value);
            }
          });
          if (minSel) {
            fill(minSel, filter, minValue);
            sortOffsetOptions(minSel);
          }
          if (maxSel) {
            fill(maxSel, filter, maxValue);
            sortOffsetOptions(maxSel);
          }
        }
      }

      container.dispatchEvent(new CustomEvent('saikospeed:instrument:sync'));
    }

    // Rebuild the category cascade selects from a collection.filters array
    // (the embedded JSON, or a staged preview fetch): fresh scoped counts,
    // zero-count values pruned, the disabled chain mirroring the server
    // render (child waits on parent, product type waits on subcategory).
    function refreshCategorySelectsFrom(root, container, filters) {
      const selects = root.querySelectorAll('.saikospeed-cat-select');
      if (!selects.length) return;
      if (!Array.isArray(filters) || !filters.length) return;
      let parentSel = null;
      let childSel = null;
      let subSel = null;
      Array.prototype.forEach.call(selects, function (selectEl) {
        const paramName = selectEl.getAttribute('name') || '';
        if (!paramName) return;
        if (paramName.indexOf('menu_category_parent') !== -1) parentSel = selectEl;
        else if (paramName.indexOf('menu_child') !== -1) childSel = selectEl;
        else if (paramName.indexOf('menu_category') !== -1) subSel = selectEl;
        const filter = filters.find(function (f) {
          return String((f && f.param_name) || '') === paramName;
        });
        if (!filter || !Array.isArray(filter.values)) return;
        const current = String(selectEl.value || '');
        const placeholder =
          selectEl.options && selectEl.options.length ? String(selectEl.options[0].text || '') : 'All';
        while (selectEl.firstChild) selectEl.removeChild(selectEl.firstChild);
        const opt0 = document.createElement('option');
        opt0.value = '';
        opt0.textContent = placeholder;
        selectEl.appendChild(opt0);
        filter.values.forEach(function (v) {
          if (!v) return;
          const isActive = !!v.active;
          const count = Number(v.count || 0);
          if (!isActive && count <= 0) return;
          const opt = document.createElement('option');
          opt.value = v.value != null ? String(v.value) : '';
          opt.textContent = String(v.label != null ? v.label : v.value) + ' (' + count + ')';
          if (isActive || String(opt.value) === current) opt.selected = true;
          selectEl.appendChild(opt);
        });
        if (!selectEl.selectedOptions.length) selectEl.value = current;
      });
      if (childSel && parentSel) childSel.disabled = !parentSel.value;
      if (subSel && childSel) subSel.disabled = !childSel.value;
      container.dispatchEvent(new CustomEvent('saikospeed:instrument:sync'));
    }

    function initInstrument(container) {
      const bar = container.querySelector('[data-saikospeed-instrument]');
      if (!bar) return;
      if (bar.__saikoInstrumentInit === true) return;
      bar.__saikoInstrumentInit = true;
      const segs = Array.prototype.slice.call(bar.querySelectorAll('[data-instrument-seg]'));
      const finePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;

      // Mobile summary row: below 750px the bar rests collapsed as one row
      // and this button carries the whole state; tapping it expands the
      // stacked segments in place.
      const summaryBtn = bar.querySelector('[data-instrument-summary]');
      const summaryValueEl = summaryBtn ? summaryBtn.querySelector('[data-instrument-summary-value]') : null;
      const summaryLabelEl = summaryBtn ? summaryBtn.querySelector('[data-instrument-summary-label]') : null;
      const summaryChassis = container.getAttribute('data-chassis-mode') === 'true';
      const summaryWheels = container.getAttribute('data-wheels-mode') === 'true';

      function selectedSummaryLabel(select) {
        if (!select || !select.value) return '';
        const opt = select.selectedOptions && select.selectedOptions[0];
        if (!opt) return '';
        return opt.textContent.trim().replace(/\s*\(\d+\)$/, '');
      }

      function updateInstrumentSummary() {
        if (!summaryBtn || !summaryValueEl) return;
        const parts = [];
        if (summaryWheels) {
          let offsetDone = false;
          Array.prototype.forEach.call(
            container.querySelectorAll(
              '.saikospeed-wheel-select, .saikospeed-wheel-offset-min, .saikospeed-wheel-offset-max'
            ),
            function (sel) {
              const isOffset =
                sel.classList.contains('saikospeed-wheel-offset-min') ||
                sel.classList.contains('saikospeed-wheel-offset-max');
              if (isOffset) {
                if (offsetDone) return;
                offsetDone = true;
                const minLabel = selectedSummaryLabel(container.querySelector('.saikospeed-wheel-offset-min'));
                const maxLabel = selectedSummaryLabel(container.querySelector('.saikospeed-wheel-offset-max'));
                if (minLabel && maxLabel) {
                  parts.push(minLabel === maxLabel ? minLabel : minLabel + ' to ' + maxLabel);
                } else if (minLabel) {
                  parts.push('from ' + minLabel);
                } else if (maxLabel) {
                  parts.push('to ' + maxLabel);
                }
                return;
              }
              const label = selectedSummaryLabel(sel);
              if (label) parts.push(label);
            }
          );
          summaryValueEl.textContent = parts.length ? parts.join(' · ') : 'All wheels';
          summaryBtn.classList.toggle('is-set', parts.length > 0);
          return;
        }
        if (summaryChassis) {
          Array.prototype.forEach.call(container.querySelectorAll('.saikospeed-cat-select'), function (sel) {
            const label = selectedSummaryLabel(sel);
            if (label) parts.push(label);
          });
          const yearSel = container.querySelector('.hrllc-grid-v1-chassis-year');
          if (yearSel && yearSel.value) parts.push(yearSel.value);
          summaryValueEl.textContent = parts.length ? parts.join(' · ') : 'All categories';
        } else {
          [
            container.querySelector('.hrllc-grid-v1-year'),
            container.querySelector('.hrllc-grid-v1-make'),
            container.querySelector('.hrllc-grid-v1-model'),
          ].forEach(function (sel) {
            const label = selectedSummaryLabel(sel);
            if (label) parts.push(label);
          });
          if (summaryLabelEl) summaryLabelEl.textContent = parts.length === 3 ? 'Your vehicle' : 'Shop by vehicle';
          if (parts.length === 3) {
            // A complete vehicle reads as one name, matching the server
            // render and the fit lines; dots mark partial states only.
            summaryValueEl.textContent = parts.join(' ');
          } else {
            summaryValueEl.textContent = parts.length
              ? parts.join(' · ')
              : 'All years · All makes · All models';
          }
        }
        summaryBtn.classList.toggle('is-set', parts.length > 0);
      }

      // ----------------------------------------------------------------
      // Bottom sheet (mobile): both collapsed-row controls open one sheet
      // that stages its edits; "Show {n} products" commits them through
      // the normal render path, scrim/Escape cancels them.
      // ----------------------------------------------------------------
      const sheetEl = bar.querySelector('[data-saikospeed-sheet]');
      const sheetScrim = container.querySelector('[data-sheet-scrim]');
      const sheetApply = sheetEl ? sheetEl.querySelector('[data-sheet-apply]') : null;
      const sheetCountEl = sheetEl ? sheetEl.querySelector('[data-sheet-count]') : null;
      const sheetCountUnitEl = sheetEl ? sheetEl.querySelector('[data-sheet-count-unit]') : null;
      const filtersTrigger = bar.querySelector('[data-sheet-open]');
      const sheetFiltersDetails = sheetEl ? sheetEl.querySelector('details[data-saikospeed-filters]') : null;
      const sheetSectionId = container.getAttribute('data-section-id') || '';
      const sheetRoot = container.closest('#shopify-section-' + sheetSectionId) || document;
      let sheetSnapshot = null;
      let sheetPreviewTimer = null;
      let sheetPreviewSeq = 0;

      function setSheetCount(n) {
        // Shopify search caps results_count at 1000; on the search
        // template that value is a floor, not a count. Collection pages
        // report real uncapped counts and stay exact.
        const capped = n >= 1000 && !!container.closest('.template-search');
        if (sheetCountEl) sheetCountEl.textContent = capped ? '1000+' : String(n);
        if (sheetCountUnitEl) sheetCountUnitEl.textContent = n === 1 ? 'product' : 'products';
      }

      function resetSheetCountFromPage() {
        const countEl = document.getElementById('ProductCount');
        if (!countEl) return;
        const n = parseInt(String(countEl.textContent || '').replace(/[^0-9]/g, ''), 10);
        if (!isNaN(n)) setSheetCount(n);
      }

      async function runSheetPreview() {
        const seq = ++sheetPreviewSeq;
        if (!sheetSectionId || sheetStagedParams === null) return;
        const url =
          window.location.pathname +
          '?section_id=' +
          encodeURIComponent(sheetSectionId) +
          (sheetStagedParams ? '&' + sheetStagedParams : '');
        let doc = null;
        try {
          const res = await fetch(url, { credentials: 'same-origin' });
          if (!res.ok) return;
          doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        } catch (e) {
          return;
        }
        if (seq !== sheetPreviewSeq || !sheetStagingActive) return;
        const countEl = doc.querySelector('#ProductCount');
        if (countEl) {
          const n = parseInt(String(countEl.textContent || '').replace(/[^0-9]/g, ''), 10);
          if (!isNaN(n)) setSheetCount(n);
        }
        const jsonEl = doc.querySelector('.hrllc-grid-v1-collection-filters');
        const filters = jsonEl ? safeJsonParse(jsonEl.textContent || '[]') : null;
        if (Array.isArray(filters) && filters.length) {
          refreshCategorySelectsFrom(sheetRoot, container, filters);
          refreshWheelSelectsFrom(sheetRoot, container, filters);
        }
      }

      function scheduleSheetPreview() {
        if (sheetPreviewTimer) window.clearTimeout(sheetPreviewTimer);
        sheetPreviewTimer = window.setTimeout(runSheetPreview, 250);
      }

      function openSheet(anchor) {
        if (!sheetEl || container.classList.contains('is-mobile-expanded')) return;
        sheetSnapshot = Array.prototype.map.call(bar.querySelectorAll('select, input'), function (el) {
          return { el: el, value: el.value, checked: el.checked };
        });
        sheetStagingActive = true;
        sheetStagedParams = null;
        sheetDeferredSelection = null;
        sheetStagingNotify = scheduleSheetPreview;
        container.classList.add('is-mobile-expanded');
        if (summaryBtn) summaryBtn.setAttribute('aria-expanded', 'true');
        if (sheetFiltersDetails) sheetFiltersDetails.setAttribute('open', '');
        sheetEl.setAttribute('role', 'dialog');
        sheetEl.setAttribute('aria-modal', 'true');
        sheetEl.setAttribute('aria-label', 'Refine this collection');
        sheetEl.setAttribute('tabindex', '-1');
        document.documentElement.classList.add('saikospeed-sheet-lock');
        resetSheetCountFromPage();
        try {
          sheetEl.focus({ preventScroll: true });
        } catch (e) {}
        if (anchor === 'filters' && sheetFiltersDetails) {
          sheetEl.scrollTop = sheetFiltersDetails.offsetTop;
        } else {
          sheetEl.scrollTop = 0;
        }
      }

      function closeSheet(commit) {
        if (!sheetEl || !container.classList.contains('is-mobile-expanded')) return;
        const staged = sheetStagedParams;
        const deferred = sheetDeferredSelection;
        sheetStagingActive = false;
        sheetStagingNotify = null;
        sheetStagedParams = null;
        sheetDeferredSelection = null;
        sheetPreviewSeq++;
        if (sheetPreviewTimer) window.clearTimeout(sheetPreviewTimer);
        container.classList.remove('is-mobile-expanded');
        if (summaryBtn) summaryBtn.setAttribute('aria-expanded', 'false');
        if (sheetFiltersDetails) sheetFiltersDetails.removeAttribute('open');
        sheetEl.removeAttribute('role');
        sheetEl.removeAttribute('aria-modal');
        sheetEl.removeAttribute('aria-label');
        sheetEl.removeAttribute('tabindex');
        document.documentElement.classList.remove('saikospeed-sheet-lock');
        if (commit) {
          if (deferred) persistSelection(deferred.container, deferred.selection);
          if (staged !== null) renderFacetsPage(staged, true);
        } else if (sheetSnapshot) {
          sheetSnapshot.forEach(function (s) {
            s.el.value = s.value;
            if (s.el.type === 'checkbox') s.el.checked = s.checked;
          });
          const fp = container.getAttribute('data-fitment-filter-param') || '';
          if (fp) applyShopifyFitmentFilter(container, getCurrentUrlParamValues(fp), true);
          container.dispatchEvent(new CustomEvent('saikospeed:sheet:cancel'));
          container.dispatchEvent(new CustomEvent('saikospeed:instrument:sync'));
        }
        sheetSnapshot = null;
        if (summaryBtn) {
          try {
            summaryBtn.focus({ preventScroll: true });
          } catch (e) {}
        }
      }

      if (summaryBtn) {
        summaryBtn.addEventListener('click', function () {
          if (container.classList.contains('is-mobile-expanded')) closeSheet(false);
          else openSheet();
        });
        updateInstrumentSummary();
      }

      if (filtersTrigger) {
        // No Filters section (e.g. /collections/all, where every row's
        // data source is capped out): the trigger stands down and the
        // summary alone opens the sheet.
        if (!sheetFiltersDetails) filtersTrigger.style.display = 'none';
        filtersTrigger.addEventListener('click', function () {
          openSheet('filters');
        });
      }

      if (sheetScrim) {
        sheetScrim.addEventListener('click', function () {
          closeSheet(false);
        });
      }

      if (sheetApply) {
        sheetApply.addEventListener('click', function () {
          closeSheet(true);
        });
      }

      container.addEventListener('saikospeed:sheet:close', function () {
        closeSheet(false);
      });

      segs.forEach(function (seg) {
        const select = seg.querySelector('select');
        if (!select) return;
        select.addEventListener('change', function () {
          syncInstrumentSegment(seg, select);
          updateInstrumentSummary();
        });
        syncInstrumentSegment(seg, select);

        if (!finePointer) return;
        const panel = document.createElement('div');
        panel.className = 'saikospeed-instrument__panel';
        panel.setAttribute('role', 'listbox');
        const label = seg.querySelector('.saikospeed-instrument__label');
        if (label) panel.setAttribute('aria-label', label.textContent);
        seg.appendChild(panel);
        seg.addEventListener('click', function (event) {
          if (event.target.closest('.saikospeed-instrument__panel')) return;
          const wasOpen = seg.classList.contains('is-open');
          closeInstrumentPanels();
          if (!wasOpen && !select.disabled) {
            // Show first, then build: a display:none panel measures zero, so
            // scrolling it to the current value would silently do nothing.
            // Both happen in one frame, so nothing paints in between.
            seg.classList.add('is-open');
            buildInstrumentPanel(seg, select, panel);
          }
        });
      });

      // The chassis wiring pokes select values programmatically (cookie
      // prefill, model reveal, clears); it announces those through this
      // event so the facades never go stale.
      container.addEventListener('saikospeed:instrument:sync', function () {
        segs.forEach(function (seg) {
          const select = seg.querySelector('select');
          if (select) syncInstrumentSegment(seg, select);
        });
        updateInstrumentSummary();
      });
    }

    function initOne(container) {
      if (!container) return;
      if (container.__hrllcVehicleInit === true) return;
      container.__hrllcVehicleInit = true;

      const root = container.closest('#shopify-section-' + container.getAttribute('data-section-id')) || document;
      const filterMode = container.getAttribute('data-filter-mode') || 'shopify_tokens';
      const showTrim = (container.getAttribute('data-show-trim') || '0') === '1';
      const fitmentParam = container.getAttribute('data-fitment-filter-param') || '';

      if (isFitmentFilterDebugEnabled()) {
        const domCount = fitmentParam ? getFitmentFilterValuesFromDom(fitmentParam).length : 0;
        console.log('[Vehicle Fitment] Init', {
          sectionId: container.getAttribute('data-section-id'),
          filterMode,
          showTrim,
          fitmentParam,
          domFacetValueCount: domCount,
        });
      }

      const yearEl = container.querySelector('.hrllc-grid-v1-year');
      const makeEl = container.querySelector('.hrllc-grid-v1-make');
      const modelEl = container.querySelector('.hrllc-grid-v1-model');
      const trimEl = container.querySelector('.hrllc-grid-v1-trim');
      const fitmentCodeEl = container.querySelector('.hrllc-grid-v1-fitment-code');
      const brandEl = root.querySelector('.hrllc-grid-v1-brand');
      const wheelsDiameterEl = container.querySelector('.hrllc-grid-wheels-diameter');
      const wheelsWidthEl = container.querySelector('.hrllc-grid-wheels-width');
      const wheelsOffsetEl = container.querySelector('.hrllc-grid-wheels-offset');
      const wheelsPatternEl = container.querySelector('.hrllc-grid-wheels-pattern');
      const universalToggleEl = root.querySelector('.hrllc-grid-v1-universal-toggle');
      // Both Clear all controls (the desktop row action and the mobile
      // sheet-foot action) share one handler.
      const clearBtns = Array.prototype.slice.call(container.querySelectorAll('.hrllc-grid-v1-clear'));
      const clearBtn = clearBtns[0] || null;
      const sortEl = root.querySelector('#collection-sort-order');
      const perPageSelectEl = root.querySelector('#collection-per-page');

      const brandParam = container.getAttribute('data-brand-filter-param') || '';

      function findUniversalFitmentValue() {
        if (!fitmentParam) return '';
        const tokens = getAvailableFitmentValues(container, fitmentParam);
        for (const raw of tokens) {
          const decoded = decodeTokenForParsing(raw);
          if (String(decoded || '').trim().toLowerCase() === 'universal') return raw;
        }
        // Fallback: look for a partial match (e.g. "Universal Fit"), but only if no exact match.
        for (const raw of tokens) {
          const decoded = decodeTokenForParsing(raw);
          if (String(decoded || '').trim().toLowerCase().includes('universal')) return raw;
        }
        return '';
      }

      function isUniversalValueActive() {
        if (!fitmentParam) return false;
        const urlValues = getCurrentUrlParamValues(fitmentParam);
        if (!urlValues.length) return false;
        return urlValues.some((v) => String(decodeTokenForParsing(v) || '').toLowerCase() === 'universal');
      }

      function syncUniversalToggleFromUrl() {
        if (!universalToggleEl || !fitmentParam) return;
        universalToggleEl.checked = isUniversalValueActive();
      }

      function setUniversalEnabled(enabled) {
        if (!fitmentParam) return;

        if (enabled) {
          const universalValue = findUniversalFitmentValue();
          if (!universalValue) {
            if (isFitmentFilterDebugEnabled()) {
              console.warn('[Vehicle Fitment] Universal toggle ON but no "Universal" fitment value exists');
            }
            if (universalToggleEl) universalToggleEl.checked = false;
            return;
          }

          // Add Universal as an additional fitment value (OR) without overriding current vehicle.
          try {
            const params = new URLSearchParams(window.location.search || '');
            const currentValues = params.getAll(fitmentParam).map((v) => String(v || '').trim()).filter(Boolean);
            const hasUniversal = currentValues.some((v) => String(decodeTokenForParsing(v) || '').toLowerCase() === 'universal');
            if (!hasUniversal) {
              params.append(fitmentParam, universalValue);
            }
            renderFacetsPage(params.toString(), true);
          } catch (e) {
            // Fallback: if something goes wrong, at least apply universal alone.
            applyShopifyFitmentFilter(container, universalValue);
          }
          return;
        }

        // OFF: remove Universal value but keep all other fitment values.
        try {
          const params = new URLSearchParams(window.location.search || '');
          const currentValues = params.getAll(fitmentParam).map((v) => String(v || '').trim()).filter(Boolean);
          const nextValues = currentValues.filter(
            (v) => String(decodeTokenForParsing(v) || '').toLowerCase() !== 'universal'
          );
          params.delete(fitmentParam);
          nextValues.forEach((v) => params.append(fitmentParam, v));
          renderFacetsPage(params.toString(), true);
        } catch (e) {
          // Fallback: clear fitment.
          applyShopifyFitmentFilter(container, '');
        }
      }

      function clearBrandFromUrlIfPresent() {
        // Prevent loops: this function triggers a facets render. Only allow it to run once
        // per page load.
        try {
          if (window.__hrllcBrandAutoClearRan === true) return;
          window.__hrllcBrandAutoClearRan = true;
        } catch (e) {}

        if (!brandParam) return;
        const urlBrandInitial = getCurrentUrlParamValue(brandParam);
        if (!urlBrandInitial) return;

        const form = document.getElementById('FacetsFilterForm');
        if (!form) {
          window.setTimeout(clearBrandFromUrlIfPresent, 50);
          return;
        }

        if (brandEl) brandEl.value = '';
        const input = document.querySelector(
          '.hrllc-grid-v1-brand-input[name="' + CSS.escape(brandParam) + '"]'
        );
        if (input) {
          input.value = '';
          input.setAttribute('disabled', 'disabled');
        }

        // Build from the current URL (FormData drops disabled-input params
        // like the applied vehicle) and remove only the brand param.
        const params = new URLSearchParams(window.location.search || '');
        params.delete(brandParam);
        renderFacetsPage(params.toString(), true);
      }

      clearBrandFromUrlIfPresent();

      syncUniversalToggleFromUrl();

      function syncWheelsDropdownsFromUrl() {
        try {
          if (wheelsDiameterEl) {
            wheelsDiameterEl.value = getCurrentUrlParamValue(wheelsDiameterEl.getAttribute('name') || '') || '';
          }
          if (wheelsWidthEl) {
            wheelsWidthEl.value = getCurrentUrlParamValue(wheelsWidthEl.getAttribute('name') || '') || '';
          }
          if (wheelsOffsetEl) {
            wheelsOffsetEl.value = getCurrentUrlParamValue(wheelsOffsetEl.getAttribute('name') || '') || '';
          }
          if (wheelsPatternEl) {
            wheelsPatternEl.value = getCurrentUrlParamValue(wheelsPatternEl.getAttribute('name') || '') || '';
          }
        } catch (e) {}
      }

      function refreshWheelDropdownAvailabilityFromJson() {
        const filters = getCollectionFiltersDebug(container);
        if (!Array.isArray(filters) || !filters.length) return;
        const byParam = new Map(filters.map((f) => [String((f && f.param_name) || ''), f]));

        if (wheelsDiameterEl) {
          const p = String(wheelsDiameterEl.getAttribute('name') || '').trim();
          if (p && byParam.has(p)) rebuildWheelSelectOptions(wheelsDiameterEl, byParam.get(p));
        }
        if (wheelsWidthEl) {
          const p = String(wheelsWidthEl.getAttribute('name') || '').trim();
          if (p && byParam.has(p)) rebuildWheelSelectOptions(wheelsWidthEl, byParam.get(p));
        }
        if (wheelsOffsetEl) {
          const p = String(wheelsOffsetEl.getAttribute('name') || '').trim();
          if (p && byParam.has(p)) rebuildWheelSelectOptions(wheelsOffsetEl, byParam.get(p));
        }
        if (wheelsPatternEl) {
          const p = String(wheelsPatternEl.getAttribute('name') || '').trim();
          if (p && byParam.has(p)) rebuildWheelSelectOptions(wheelsPatternEl, byParam.get(p));
        }
      }

      function logWheelFiltersToConsole(context) {
        if (!isFitmentFilterDebugEnabled()) return;
        if (!wheelsDiameterEl && !wheelsWidthEl && !wheelsOffsetEl && !wheelsPatternEl) return;

        const filters = getCollectionFiltersDebug(container);
        if (!Array.isArray(filters) || !filters.length) {
          console.log('[Wheel Filters] No collection.filters JSON found', {
            context,
            sectionId: container.getAttribute('data-section-id'),
          });
          return;
        }

        function summarizeFilterByParam(paramName) {
          if (!paramName) return null;
          const f = filters.find((x) => String((x && x.param_name) || '') === String(paramName));
          if (!f) return { param_name: paramName, found: false };
          const values = Array.isArray(f.values) ? f.values : [];
          const active = values.filter((v) => v && v.active === true);
          return {
            param_name: f.param_name,
            label: f.label,
            type: f.type,
            found: true,
            value_count: values.length,
            active_count: active.length,
            zero_count_values: values.filter((v) => v && Number((v && v.count) || 0) === 0 && v.active !== true)
              .length,
          };
        }

        const diameterParam = wheelsDiameterEl ? String(wheelsDiameterEl.getAttribute('name') || '').trim() : '';
        const widthParam = wheelsWidthEl ? String(wheelsWidthEl.getAttribute('name') || '').trim() : '';
        const offsetParam = wheelsOffsetEl ? String(wheelsOffsetEl.getAttribute('name') || '').trim() : '';
        const patternParam = wheelsPatternEl ? String(wheelsPatternEl.getAttribute('name') || '').trim() : '';

        const summaries = [
          summarizeFilterByParam(diameterParam),
          summarizeFilterByParam(widthParam),
          summarizeFilterByParam(offsetParam),
          summarizeFilterByParam(patternParam),
        ].filter(Boolean);

        console.groupCollapsed('[Wheel Filters] Snapshot', {
          context,
          sectionId: container.getAttribute('data-section-id'),
          url: window.location.href,
        });
        try {
          console.table(summaries);
        } catch (e) {
          console.log(summaries);
        }
        console.groupEnd();
      }

      if (wheelsDiameterEl || wheelsWidthEl || wheelsOffsetEl || wheelsPatternEl) {
        syncWheelsDropdownsFromUrl();
        refreshWheelDropdownAvailabilityFromJson();
        logWheelFiltersToConsole('init');

        const allWheelSelects = [wheelsDiameterEl, wheelsWidthEl, wheelsOffsetEl, wheelsPatternEl];

        if (wheelsDiameterEl) {
          wheelsDiameterEl.addEventListener('change', function () {
            applyWheelSelectsAjax(allWheelSelects);
          });
        }
        if (wheelsWidthEl) {
          wheelsWidthEl.addEventListener('change', function () {
            applyWheelSelectsAjax(allWheelSelects);
          });
        }
        if (wheelsOffsetEl) {
          wheelsOffsetEl.addEventListener('change', function () {
            applyWheelSelectsAjax(allWheelSelects);
          });
        }
        if (wheelsPatternEl) {
          wheelsPatternEl.addEventListener('change', function () {
            applyWheelSelectsAjax(allWheelSelects);
          });
        }

        window.addEventListener('popstate', syncWheelsDropdownsFromUrl);
        window.addEventListener('pageshow', syncWheelsDropdownsFromUrl);

        try {
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            const sectionId = container.getAttribute('data-section-id') || '';
            subscribe(PUB_SUB_EVENTS.sectionRefreshed, function (payload) {
              if (!payload || !sectionId) return;
              if (String(payload.sectionId || '') !== String(sectionId)) return;
              syncWheelsDropdownsFromUrl();
              refreshWheelDropdownAvailabilityFromJson();
              logWheelFiltersToConsole('sectionRefreshed');
            });
          }
        } catch (e) {}
      }

      if (universalToggleEl && fitmentParam) {
        universalToggleEl.addEventListener('change', function () {
          setUniversalEnabled(!!universalToggleEl.checked);
        });
      }

      let cachedAllPageVendors = [];
      let cachedVendorIndexKey = null;
      let vendorIndexPromise = null;

      let lastFitmentKeyForBrandCache = null;

      function getFitmentKeyFromUrl() {
        if (!fitmentParam) return '';
        try {
          return String(getCurrentUrlParamValue(fitmentParam) || '').trim();
        } catch (e) {
          return '';
        }
      }

      function getVendorIndexKeyForFitment(fitmentValue) {
        const v = String(fitmentValue || '').trim();
        return v ? v : '__all__';
      }

      function getVendorIndexParamsForFitment(fitmentValue) {
        if (!fitmentParam) return '';
        const v = String(fitmentValue || '').trim();
        if (!v) return '';
        try {
          const params = new URLSearchParams();
          params.set(fitmentParam, v);
          return params.toString();
        } catch (e) {
          return '';
        }
      }

      async function ensureAllPageVendorsIndexed(force, fitmentValue) {
        if (!brandEl || !brandParam) return [];

        const key = getVendorIndexKeyForFitment(fitmentValue);
        if (!force && cachedVendorIndexKey !== null && key === cachedVendorIndexKey && cachedAllPageVendors.length) {
          return cachedAllPageVendors;
        }

        if (!force && vendorIndexPromise) return vendorIndexPromise;

        vendorIndexPromise = (async function () {
          cachedVendorIndexKey = key;
          const sectionId = container.getAttribute('data-section-id') || '';
          if (!sectionId) return [];

          const maxPage = getMaxPageFromPagination(root);
          const vendorIndexExtra = getVendorIndexParamsForFitment(fitmentValue);
          const all = [];
          const seen = new Set();
          for (let p = 1; p <= Math.max(1, maxPage); p++) {
            let vendors = [];
            try {
              vendors = await fetchSectionPageVendors(sectionId, p, vendorIndexExtra);
            } catch (e) {
              vendors = [];
            }
            for (const v of vendors) {
              if (!v) continue;
              if (seen.has(v)) continue;
              seen.add(v);
              all.push(v);
            }
          }

          all.sort();
          cachedAllPageVendors = all;
          return cachedAllPageVendors;
        })();

        try {
          return await vendorIndexPromise;
        } finally {
          vendorIndexPromise = null;
        }
      }

      async function refreshBrandOptions(forceIndex) {
        if (!brandEl || !brandParam) return;

        // Prefer the collection.filters JSON (embedded by our sections) so we can
        // prune 0-count brands even when the sidebar facets DOM isn't present.
        let options = [];
        try {
          const filtersJson = getCollectionFiltersDebug(container);
          if (Array.isArray(filtersJson) && filtersJson.length) {
            const f = filtersJson.find((x) => String((x && x.param_name) || '') === String(brandParam));
            const values = f && Array.isArray(f.values) ? f.values : [];
            if (values.length) {
              options = values
                .filter((v) => {
                  if (!v) return false;
                  if (v.active === true) return true;
                  return Number(v.count || 0) > 0;
                })
                .map((v) => ({
                  value: String(v.value || '').trim(),
                  label: String(v.label || v.value || '').trim(),
                }))
                .filter((o) => o.value);
            }
          }
        } catch (e) {
          options = [];
        }

        // Prefer Shopify Search & Discovery facet values so the dropdown matches availability.
        // Remove disabled values (these correspond to unavailable/0-count options).
        const facetSelector =
          'input[type="checkbox"][form="FacetsFilterForm"][name="' + CSS.escape(brandParam) + '"]';
        const facetInputs = document.querySelectorAll(facetSelector);
        if (!options.length && facetInputs && facetInputs.length) {
          options = Array.from(facetInputs)
            .filter((i) => !i.disabled || i.checked)
            .map((i) => ({
              value: String(i.value || '').trim(),
              label: String(i.value || '').trim(),
            }))
            .filter((o) => o.value);
        }

        // Fallback: older behavior (index all vendors across pages).
        if (!options.length) {
          const fitmentValue = getFitmentKeyFromUrl();
          const vendors = await ensureAllPageVendorsIndexed(!!forceIndex, fitmentValue);
          options = (vendors || []).map((v) => ({ value: v, label: v }));
        }

        if (!options.length) return;

        // Remove duplicates (by param value) and sort labels for stable UI.
        options = Array.from(new Map(options.map((o) => [o.value, o])).values()).sort((a, b) =>
          String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' })
        );

        const current = brandEl.value || '';
        setSelectOptions(brandEl, options, 'Brand');
        if (current && options.some((o) => o.value === current)) brandEl.value = current;
        else brandEl.value = '';
        container.dispatchEvent(new CustomEvent('saikospeed:instrument:sync'));
      }

      // The category cascade selects are server-rendered with scoped counts;
      // AJAX filtering (vehicle, brand, toggles) re-renders the embedded
      // collection.filters JSON but not the selects, so their facades and
      // panels would keep pre-filter counts. Rebuild them from the fresh JSON.
      function refreshCategoryOptions() {
        refreshCategorySelectsFrom(root, container, getCollectionFiltersDebug(container));
      }

      let facetRefreshTimer = null;
      function scheduleBrandRefresh(forceIndex) {
        if (facetRefreshTimer) window.clearTimeout(facetRefreshTimer);
        facetRefreshTimer = window.setTimeout(function () {
          refreshBrandOptions(!!forceIndex);
        }, 50);
      }

      let categoryRefreshTimer = null;
      function scheduleCategoryRefresh() {
        if (categoryRefreshTimer) window.clearTimeout(categoryRefreshTimer);
        categoryRefreshTimer = window.setTimeout(function () {
          try {
            refreshCategoryOptions();
          } catch (e) {}
        }, 50);
      }

      // Refresh brands after facets.js finishes updating the DOM.
      // Reset/pagination/facet-pill removals all render asynchronously; relying on timeouts
      // can be flaky. facets.js publishes PUB_SUB_EVENTS.sectionRefreshed after render.
      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          const sectionId = container.getAttribute('data-section-id') || '';
          subscribe(PUB_SUB_EVENTS.sectionRefreshed, function (payload) {
            if (!payload || !sectionId) return;
            if (String(payload.sectionId || '') !== String(sectionId)) return;

            const currentFitment = getFitmentKeyFromUrl();
            const currentKey = getVendorIndexKeyForFitment(currentFitment);
            const shouldInvalidate = lastFitmentKeyForBrandCache === null || String(currentKey) !== String(lastFitmentKeyForBrandCache);
            if (shouldInvalidate) {
              cachedVendorIndexKey = null;
              cachedAllPageVendors = [];
              lastFitmentKeyForBrandCache = currentKey;
            }

            scheduleBrandRefresh(shouldInvalidate);
            scheduleCategoryRefresh();
            scheduleWheelRefresh();

            try {
              logFacetDomFilters(container);
            } catch (e) {}

            // Keep brand dropdown in sync when pills are removed/added via AJAX.
            // This covers clicking the "X" on the active brand filter pill.
            try {
              syncBrandFromUrl();
            } catch (e) {}
          });
        }
      } catch (e) {}

      if (sortEl) {
        sortEl.addEventListener('change', function () {
          // Build from the current URL (FormData drops disabled-input
          // params like the applied vehicle) and touch only the sort.
          const params = new URLSearchParams(currentParamsBase());
          if (sortEl.value) params.set('sort_by', sortEl.value);
          else params.delete('sort_by');
          params.delete('page');
          renderFacetsPage(params.toString(), true);
        });
      }

      // Category cascade, availability and price apply like every other
      // filter: URL surgery plus AJAX render. (These were native form
      // submits before the facade, count and option refreshers made the
      // band honest after AJAX; one path also lets the sheet stage them.)
      function bindParamControl(el, mutate) {
        if (!el) return;
        el.addEventListener('change', function () {
          const params = new URLSearchParams(currentParamsBase());
          mutate(params, el);
          params.delete('page');
          renderFacetsPage(params.toString(), true);
        });
      }

      Array.prototype.forEach.call(root.querySelectorAll('.saikospeed-cat-select'), function (sel) {
        bindParamControl(sel, function (params) {
          if (sel.value) params.set(sel.name, sel.value);
          else params.delete(sel.name);
        });
      });

      const availabilityEl = root.querySelector('.hrllc-grid-v1-availability-toggle');
      bindParamControl(availabilityEl, function (params, el) {
        if (el.checked) params.set(el.name, el.value || '1');
        else params.delete(el.name);
      });

      Array.prototype.forEach.call(root.querySelectorAll('.saikospeed-price-facet__input'), function (inp) {
        bindParamControl(inp, function (params, el) {
          if (el.name && el.value !== '') params.set(el.name, el.value);
          else if (el.name) params.delete(el.name);
        });
      });

      // Wheels spec instrument: the single-value segments apply their own
      // param; the offset pair applies every stocked offset inside the
      // chosen window as repeated OR'd filter values ("between +25 and
      // +40" is the union of the real offsets in that window). An empty
      // window applies an impossible sentinel so the grid honestly shows
      // zero instead of silently dropping the filter.
      Array.prototype.forEach.call(root.querySelectorAll('.saikospeed-wheel-select'), function (sel) {
        bindParamControl(sel, function (params) {
          if (sel.value) params.set(sel.name, sel.value);
          else params.delete(sel.name);
        });
      });

      const wheelOffsetMinEl = root.querySelector('.saikospeed-wheel-offset-min');
      const wheelOffsetMaxEl = root.querySelector('.saikospeed-wheel-offset-max');
      if (wheelOffsetMinEl || wheelOffsetMaxEl) {
        const offsetParamName =
          (wheelOffsetMinEl && wheelOffsetMinEl.getAttribute('data-offset-param')) ||
          (wheelOffsetMaxEl && wheelOffsetMaxEl.getAttribute('data-offset-param')) ||
          '';

        const applyWheelOffsetRange = function () {
          if (!offsetParamName) return;
          const min = wheelOffsetMinEl ? parseOffsetNumber(wheelOffsetMinEl.value) : null;
          const max = wheelOffsetMaxEl ? parseOffsetNumber(wheelOffsetMaxEl.value) : null;
          const params = new URLSearchParams(currentParamsBase());
          params.delete(offsetParamName);
          if (min !== null || max !== null) {
            const seen = new Set();
            let any = false;
            [wheelOffsetMinEl, wheelOffsetMaxEl].forEach(function (sel) {
              if (!sel) return;
              Array.prototype.forEach.call(sel.options, function (o) {
                if (!o.value || seen.has(o.value)) return;
                seen.add(o.value);
                const n = parseOffsetNumber(o.value);
                if (n === null) return;
                if (min !== null && n < min) return;
                if (max !== null && n > max) return;
                params.append(offsetParamName, o.value);
                any = true;
              });
            });
            if (!any) params.append(offsetParamName, '__none__');
          }
          params.delete('page');
          renderFacetsPage(params.toString(), true);
        };

        const clampOffsetPair = function (changed) {
          const min = wheelOffsetMinEl ? parseOffsetNumber(wheelOffsetMinEl.value) : null;
          const max = wheelOffsetMaxEl ? parseOffsetNumber(wheelOffsetMaxEl.value) : null;
          if (min === null || max === null || min <= max) return;
          // Inverted window: the side the shopper did not just touch
          // follows the one they did.
          if (changed === wheelOffsetMinEl && wheelOffsetMaxEl) wheelOffsetMaxEl.value = wheelOffsetMinEl.value;
          else if (wheelOffsetMinEl) wheelOffsetMinEl.value = wheelOffsetMaxEl.value;
        };

        [wheelOffsetMinEl, wheelOffsetMaxEl].forEach(function (sel) {
          if (!sel) return;
          sortOffsetOptions(sel);
          sel.addEventListener('change', function () {
            clampOffsetPair(sel);
            container.dispatchEvent(new CustomEvent('saikospeed:instrument:sync'));
            applyWheelOffsetRange();
          });
        });
      }

      let wheelRefreshTimer = null;
      function scheduleWheelRefresh() {
        if (wheelRefreshTimer) window.clearTimeout(wheelRefreshTimer);
        wheelRefreshTimer = window.setTimeout(function () {
          try {
            refreshWheelSelectsFrom(root, container, getCollectionFiltersDebug(container));
          } catch (e) {}
        }, 50);
      }

      // Sheet cancel: staged YMM edits are rolled back by value snapshot;
      // this event resynchronizes the cascade options, hidden inputs and
      // enabled states against the still-applied URL without rendering
      // (nothing was applied, so there is nothing to re-render).
      container.addEventListener('saikospeed:sheet:cancel', function () {
        try {
          const selection = readSelectionFromUI(container);
          const tokens = getAvailableFitmentValues(container, fitmentParam);
          const index = buildTokenIndex(tokens, showTrim);
          populateFromTokens(index, selection);
          const selectionFinal = readSelectionFromUI(container);
          applyShopifyFitmentFilter(container, index.filterValueForSelection(selectionFinal), true);
        } catch (e) {}
        try {
          syncUniversalToggleFromUrl();
        } catch (e) {}
        try {
          updateEnabledStates(container, showTrim);
        } catch (e) {}
      });

      if (fitmentParam) {
        root.addEventListener(
          'click',
          function (event) {
          const link = event.target && event.target.closest ? event.target.closest('a') : null;
          if (!link || !link.href) return;

          const isFacetRemove = !!(link.closest && link.closest('facet-remove'));
          if (!isFacetRemove) return;

          let currentValue = '';
          try {
            currentValue = getCurrentUrlParamValue(fitmentParam) || '';
          } catch (e) {
            currentValue = '';
          }

          let nextValue = '';
          try {
            const nextUrl = new URL(link.href, window.location.origin);
            nextValue = nextUrl.searchParams.get(fitmentParam) || '';
          } catch (e) {
            nextValue = '';
          }

          if (!nextValue) {
            clearSelection(container, showTrim);
            updateEnabledStates(container, showTrim);

            const fitmentInput = document.querySelector(
              '.hrllc-grid-v1-fitment-input[name="' + CSS.escape(fitmentParam) + '"]'
            );
            if (fitmentInput) {
              fitmentInput.value = '';
              fitmentInput.setAttribute('disabled', 'disabled');
            }

            // When the vehicle filter pill is removed, the grid will re-render via facets.js.
            // Force a brand list refresh (same behavior as the Reset button) so the brand
            // dropdown updates without needing manual reset.
            cachedVendorIndexKey = null;
            cachedAllPageVendors = [];
            lastFitmentKeyForBrandCache = getVendorIndexKeyForFitment('');
            scheduleBrandRefresh(true);
          }
          },
          true
        );
      }

      // When the active brand pill is removed (facet-remove), reset the dropdown to default.
      if (brandParam && brandEl) {
        root.addEventListener(
          'click',
          function (event) {
            const link = event.target && event.target.closest ? event.target.closest('a') : null;
            if (!link || !link.href) return;

            const isFacetRemove = !!(link.closest && link.closest('facet-remove'));
            if (!isFacetRemove) return;

            let nextHasBrand = false;
            try {
              const nextUrl = new URL(link.href, window.location.origin);
              nextHasBrand = nextUrl.searchParams.getAll(brandParam).some((v) => String(v || '').trim());
            } catch (e) {
              nextHasBrand = false;
            }

            // If the next URL no longer has the brand param, reset UI immediately.
            if (!nextHasBrand) {
              brandEl.value = '';
              const input = document.querySelector(
                '.hrllc-grid-v1-brand-input[name="' + CSS.escape(brandParam) + '"]'
              );
              if (input) {
                input.value = '';
                input.setAttribute('disabled', 'disabled');
              }
            }
          },
          true
        );
      }

      logAllCollectionFilterParams(container);
      logFacetDomFilters(container);

      if (isFitmentFilterDebugEnabled()) {
        console.log('[Vehicle Fitment] Brand filter param', {
          brandParam,
        });
      }
      function syncBrandFromUrl() {
        if (!brandEl || !brandParam) return;

        const urlBrand = getCurrentUrlParamValue(brandParam);
        brandEl.value = urlBrand || '';

        const input = document.querySelector(
          '.hrllc-grid-v1-brand-input[name="' + CSS.escape(brandParam) + '"]'
        );
        if (input) {
          if (urlBrand) {
            input.value = urlBrand;
            input.removeAttribute('disabled');
          } else {
            input.value = '';
            input.setAttribute('disabled', 'disabled');
          }
        }
      }

      if (filterMode === 'fitment_code') {
        if (!fitmentCodeEl) return;

        function populateFitmentCodes(values, selection) {
          setSelectOptions(fitmentCodeEl, values, 'Any');
          fitmentCodeEl.value = selection.fitment_code || '';
        }

        function onFitmentCodeChanged(persist) {
          const selection = readSelectionFromUI(container);
          const values = getFitmentCodes(container);
          populateFitmentCodes(values, selection);

          const selectionFinal = readSelectionFromUI(container);
          applyShopifyFitmentCodeFilter(container, selectionFinal.fitment_code);

          if (persist) persistSelection(container, selectionFinal);
        }

        // Initial load: prefer URL (active filter) over cookie
        let initialSelection = loadSelection(container);
        if (fitmentParam) {
          const urlCode = getCurrentUrlParamValue(fitmentParam);
          if (urlCode) {
            initialSelection = { ...initialSelection, fitment_code: urlCode };
            persistSelection(container, initialSelection);
          }
        }

        const initialValues = getFitmentCodes(container);
        populateFitmentCodes(initialValues, initialSelection);
        setSelectionToUI(container, initialSelection, false);

        // If cookie exists but URL doesn't include the fitment filter yet, apply it once
        if (fitmentParam) {
          const urlCode = getCurrentUrlParamValue(fitmentParam);
          const desired = String(initialSelection.fitment_code || '').trim();
          if (desired && desired !== urlCode) {
            applyShopifyFitmentCodeFilter(container, desired);
          }
        }

        fitmentCodeEl.addEventListener('change', function () {
          onFitmentCodeChanged(true);
        });

        if (clearBtn) {
          clearBtn.addEventListener('click', function () {
            clearSelection(container, false);
            const selectionFinal = readSelectionFromUI(container);
            applyShopifyFitmentCodeFilter(container, selectionFinal.fitment_code);
            persistSelection(container, selectionFinal);
          });
        }

        const gridContainer = root.querySelector('#ProductGridContainer');
        if (gridContainer && window.MutationObserver) {
          const obs = new MutationObserver(function () {
            const selection = readSelectionFromUI(container);
            const values = getFitmentCodes(container);
            populateFitmentCodes(values, selection);
          });
          obs.observe(gridContainer, { childList: true, subtree: true });
        }

        return;
      }

      // Some sections (e.g. Wheels) intentionally omit the Year/Make/Model UI.
      // In that case we still want Brand + Universal controls to work, so skip vehicle token
      // initialization without aborting the rest of init.
      const hasYmmUI = !!(yearEl && makeEl && modelEl);
      if (!hasYmmUI) {
        // No vehicle token UI to bind. Still enable Brand dropdown behavior.
        if (brandEl && brandParam) {
          try {
            refreshBrandOptions();
            brandEl.value = '';
          } catch (e) {}

          try {
            brandEl.addEventListener('change', function () {
              applyShopifyBrandFilter(container, brandEl.value);
            });
          } catch (e) {}

          try {
            window.addEventListener('popstate', syncBrandFromUrl);
            window.addEventListener('pageshow', syncBrandFromUrl);
          } catch (e) {}
        }

        // The wheels instrument still needs the shared machinery the
        // vehicle path wires further down: the bar itself (summary,
        // panels, bottom sheet) and the bare-reload Clear all.
        initInstrument(container);
        // The spec line lives in the tool band, outside #ProductGridContainer,
        // so an AJAX filter render never touches it and the vehicle cannot
        // change without a page load. Once at init is enough.
        updateVehicleSpecLine(container);
        if (container.querySelector('[data-saikospeed-instrument]')) {
          clearBtns.forEach(function (btn) {
            btn.addEventListener('click', function () {
              window.location.href = bareUrlPreservingIdentity();
            });
          });
        }

        // Nothing else to bind for vehicle tokens.
        return;
      }

      function populateFromTokens(index, selection) {
        setSelectOptions(yearEl, index.years, 'Year');
        yearEl.value = selection.year || '';

        const makes = yearEl.value ? index.makesForYear(yearEl.value) : [];
        setSelectOptions(makeEl, makes, 'Make');
        makeEl.value = selection.make || '';

        const models = yearEl.value && makeEl.value ? index.modelsForYearMake(yearEl.value, makeEl.value) : [];
        setSelectOptions(modelEl, models, 'Model');
        modelEl.value = selection.model || '';

        if (showTrim && trimEl) {
          const trims =
            yearEl.value && makeEl.value && modelEl.value
              ? index.trimsForYearMakeModel(yearEl.value, makeEl.value, modelEl.value)
              : [];
          setSelectOptions(trimEl, trims, 'Any');
          trimEl.value = selection.trim || '';
        }

        updateEnabledStates(container, showTrim);

        // Every populate is a programmatic poke (cookie prefill, cascade
        // rebuilds, AJAX reindex); the instrument facades resync from it.
        container.dispatchEvent(new CustomEvent('saikospeed:instrument:sync'));
      }

      function deriveSelectionFromToken(token) {
        const decoded = decodeTokenForParsing(token);

        const range = parseRangeLabelToken(decoded);
        if (range && range.years && range.years.length) {
          return {
            year: range.years[0] || '',
            make: range.make || '',
            model: range.model || '',
            trim: '',
          };
        }

        const single = parseSingleYearLabelToken(decoded);
        if (single) {
          return {
            year: single.year || '',
            make: single.make || '',
            model: single.model || '',
            trim: '',
          };
        }

        const p = parseToken(decoded);
        if (!p) return { year: '', make: '', model: '', trim: '' };
        return {
          year: p.year,
          make: p.make,
          model: p.model,
          trim: p.trim,
        };
      }

      // Shopify reports an applied filter value as "active" only when it
      // appears in the facet's capped value list. A vehicle outside that list
      // filters the grid correctly but leaves filter.active_values empty, so
      // Liquid cannot see it and every card renders without its fit line, on
      // exactly the vehicles the exact-list cascade just made reachable. Fill
      // the line in for that case only; when the server rendered them (a
      // listed value, or a chassis collection's own line) this stands down.
      function ensureFitsLines() {
        if (!fitmentParam) return;
        const grid = document.getElementById('product-grid');
        if (!grid || grid.querySelector('.card-fits-vehicle')) return;

        const applied = getCurrentUrlParamValues(fitmentParam);
        if (!applied.length) return;

        // Prefer a pipe token: it names the exact year, where a range label
        // covering the same vehicle only names the span.
        const exact = applied.find(function (v) {
          return String(v).indexOf('|') !== -1;
        });
        const selection = deriveSelectionFromToken(exact || applied[0]);
        const label = [selection.year, selection.make, selection.model]
          .filter(Boolean)
          .join(' ')
          .trim();
        if (!label) return;

        Array.prototype.forEach.call(grid.querySelectorAll('.card-stock-count'), function (stock) {
          if (!stock.parentNode || stock.parentNode.querySelector('.card-fits-vehicle')) return;
          const line = document.createElement('div');
          line.className = 'card-fits-vehicle';
          line.textContent = 'Fits your ' + label;
          stock.insertAdjacentElement('afterend', line);
        });
      }

      function onSelectionChanged(persist) {
        if (universalToggleEl && universalToggleEl.checked) {
          universalToggleEl.checked = false;
        }
        const selection = readSelectionFromUI(container);
        const tokens = getAvailableFitmentValues(container, fitmentParam);
        logFetchedFitmentValues(container, fitmentParam, tokens);
        const index = buildTokenIndex(tokens, showTrim);

        // Print Brand refetch AFTER the expanded fitments debug output
        // (buildTokenIndex logs '[Vehicle Fitment] Expanded fitments (ALL)' when debug is enabled).
        logBrandFacetRefetch(container);

        // Keep UI options in sync with currently available tokens (which may change after Shopify filtering)
        populateFromTokens(index, selection);

        const selectionFinal = readSelectionFromUI(container);
        const filterValue = index.filterValueForSelection(selectionFinal);
        // The only user-driven apply, so the only one allowed to route.
        applyShopifyFitmentFilter(container, filterValue, false, true);

        if (persist) {
          persistSelection(container, selectionFinal);
        }
      }

      // Initial load: prefer URL (active filter) over cookie
      let initialSelection = loadSelection(container);
      if (fitmentParam) {
        const urlToken = getCurrentUrlParamValue(fitmentParam);
        if (urlToken) {
          initialSelection = deriveSelectionFromToken(urlToken);
          persistSelection(container, initialSelection);
        }
      }

      const initialTokens = getFitmentTokens(container);
      const initialAvailableTokens = getAvailableFitmentValues(container, fitmentParam);
      logFetchedFitmentValues(container, fitmentParam, initialAvailableTokens);
      const initialIndex = buildTokenIndex(initialAvailableTokens, showTrim);

      // Print Brand refetch AFTER the expanded fitments debug output
      logBrandFacetRefetch(container);
      populateFromTokens(initialIndex, initialSelection);
      setSelectionToUI(container, initialSelection, showTrim);

      // If cookie exists but URL doesn't include the fitment filter yet, apply it once
      if (fitmentParam) {
        const urlToken = getCurrentUrlParamValue(fitmentParam);
        const desiredToken = selectionToToken(initialSelection, showTrim);
        // On a chassis collection, a saved vehicle that doesn't match the
        // chassis stands down instead of strangling the grid: a Civic Si
        // owner browsing the Type R collection is here on purpose.
        const chassisModeMake = container.getAttribute('data-chassis-mode') === 'true'
          ? (container.getAttribute('data-chassis-make') || '')
          : '';
        const chassisModeModel = container.getAttribute('data-chassis-model') || '';
        const mismatchesChassis = chassisModeMake && (
          initialSelection.make !== chassisModeMake
          || (chassisModeModel && initialSelection.model !== chassisModeModel)
        );
        if (desiredToken && desiredToken !== urlToken && !mismatchesChassis) {
          // Only auto-apply when the selection is complete.
          const desiredFilterValues = initialIndex.filterValueForSelection(initialSelection);
          if (desiredFilterValues.length) applyShopifyFitmentFilter(container, desiredFilterValues);
        }
      }

      // Catalog-wide surfaces (search) have no collection to scope to, so the
      // section hands them the generated catalog asset instead. It arrives
      // async: seed the cascade from whatever the page rendered, then rebuild
      // once the full vehicle list lands. Collection pages carry their exact
      // list server-rendered and never take this path.
      const tokenSource = container.getAttribute('data-token-source') || '';
      if (tokenSource && container.getAttribute('data-tokens-exact') !== 'true') {
        loadCatalogTokens(tokenSource)
          .then(function (tokens) {
            if (!tokens.length) return;
            const island = document.querySelector(
              '.hrllc-grid-v1-fitment-tokens[data-section-id="' +
                CSS.escape(container.getAttribute('data-section-id')) + '"]'
            );
            if (!island) return;
            island.textContent = JSON.stringify(tokens);
            container.setAttribute('data-tokens-exact', 'true');
            const selection = readSelectionFromUI(container);
            populateFromTokens(buildTokenIndex(getAvailableFitmentValues(container, fitmentParam), showTrim), selection);
            setSelectionToUI(container, selection, showTrim);
            container.dispatchEvent(new CustomEvent('saikospeed:instrument:sync'));
          })
          .catch(function () {
            /* the page-rendered list still cascades */
          });
      }

      // If the URL is currently the Universal fitment, ensure the toggle matches.
      syncUniversalToggleFromUrl();

      // Brand filter: initial load prefers URL
      if (brandEl && brandParam) {
        refreshBrandOptions();
        brandEl.value = '';

        brandEl.addEventListener('change', function () {
          applyShopifyBrandFilter(container, brandEl.value);
        });

        window.addEventListener('popstate', syncBrandFromUrl);
        window.addEventListener('pageshow', syncBrandFromUrl);

        // Also refresh the available brand list on BFCache restores and history navigations.
        window.addEventListener('popstate', function () {
          scheduleBrandRefresh(false);
        });
        window.addEventListener('pageshow', function () {
          scheduleBrandRefresh(false);
        });
      }

      yearEl.addEventListener('change', function () {
        // When year changes, reset downstream
        makeEl.value = '';
        modelEl.value = '';
        if (showTrim && trimEl) trimEl.value = '';
        onSelectionChanged(true);
      });

      makeEl.addEventListener('change', function () {
        modelEl.value = '';
        if (showTrim && trimEl) trimEl.value = '';
        onSelectionChanged(true);
      });

      modelEl.addEventListener('change', function () {
        if (showTrim && trimEl) trimEl.value = '';
        onSelectionChanged(true);
      });

      if (showTrim && trimEl) {
        trimEl.addEventListener('change', function () {
          onSelectionChanged(true);
        });
      }

      clearBtns.forEach(function (clearBtn) {
        clearBtn.addEventListener('click', function () {
          // Instrument-bar "Clear all" resets the whole instrument
          // (vehicle or categories, year, secondary filters), not just
          // the vehicle: forget the saved vehicle, then reload the bare
          // page so the server re-renders every facade honestly. Sort
          // and the page's identity params (the search term) survive;
          // they are what the shopper is looking at, not filters. Legacy
          // sections (openbox) keep the AJAX reset below.
          if (container.querySelector('[data-saikospeed-instrument]')) {
            clearSelection(container, showTrim);
            window.location.href = bareUrlPreservingIdentity();
            return;
          }

          clearSelection(container, showTrim);

          cachedVendorIndexKey = null;
          cachedAllPageVendors = [];
          lastFitmentKeyForBrandCache = getVendorIndexKeyForFitment('');

          if (brandEl) brandEl.value = '';
          if (brandParam) {
            const brandInput = document.querySelector(
              '.hrllc-grid-v1-brand-input[name="' + CSS.escape(brandParam) + '"]'
            );
            if (brandInput) {
              brandInput.value = '';
              brandInput.setAttribute('disabled', 'disabled');
            }
          }

          const selectionFinal = readSelectionFromUI(container);

          const form = document.getElementById('FacetsFilterForm');
          if (form) {
            // IMPORTANT: FormData excludes disabled inputs. Our hidden brand/fitment inputs
            // are often disabled, so deleting from FormData-built params may not remove
            // the filter from the URL. Build from the current URL instead.
            const params = new URLSearchParams(window.location.search || '');
            if (fitmentParam) params.delete(fitmentParam);
            if (brandParam) params.delete(brandParam);
            renderFacetsPage(params.toString(), true);

            // Force a brand refresh even if the theme's mutation observer doesn't fire
            // for some reason during this particular render.
            scheduleBrandRefresh(true);
            window.setTimeout(function () {
              scheduleBrandRefresh(true);
            }, 400);
          } else {
            applyShopifyFitmentFilter(container, '');

            // Fallback path: still refresh brands based on the new (cleared) state.
            scheduleBrandRefresh(true);
          }

          persistSelection(container, selectionFinal);
        });
      });

      // If Shopify AJAX filtering/pagination rebuilds ProductGridContainer, reindex and reapply
      document.addEventListener('DOMContentLoaded', function () {
        // noop, already in IIFE
      });

      // Zero-result pages cannot see the applied vehicle through Liquid
      // (filter.active_values is empty when nothing matches), so the
      // vehicle-aware empty state is written here from the saved selection.
      function enhanceEmptyState() {
        const empty = document.querySelector('#ProductGridContainer .collection-empty');
        if (!empty || empty.dataset.vehicleEnhanced === 'true') return;
        const selection = loadSelection(container);
        const parts = [selection.year, selection.make, selection.model]
          .map((part) => String(part || '').trim())
          .filter(Boolean);
        if (parts.length !== 3) return;
        const vehicle = parts.join(' ');
        const heading = empty.querySelector('h2');
        const message = empty.querySelector('p');
        if (heading) heading.textContent = 'Nothing for your ' + vehicle + ' here yet';
        if (message) {
          while (message.firstChild) message.removeChild(message.firstChild);
          message.appendChild(document.createTextNode(
            "Parts in this collection haven't been confirmed for your vehicle. "
          ));
          const browseLink = document.createElement('a');
          browseLink.className = 'button-text-link';
          browseLink.href = window.location.pathname;
          browseLink.textContent = 'Browse everything here';
          message.appendChild(browseLink);
          message.appendChild(document.createTextNode(
            " or text us at (408) 800-6717 and we'll find it."
          ));
        }
        empty.dataset.vehicleEnhanced = 'true';
      }

      // Theme's facets.js replaces #ProductGridContainer.innerHTML. Rebuild dataset after that.
      // We'll hook into mutations of ProductGridContainer for this section.
      const gridContainer = root.querySelector('#ProductGridContainer');
      if (gridContainer && window.MutationObserver) {
        const obs = new MutationObserver(function () {
          // ProductGridContainer changes on facets.js AJAX updates; rebuild dropdown options
          // from the updated fitment token values embedded in the refreshed HTML.
          const selection = readSelectionFromUI(container);
          const tokens = getFitmentTokens(container);
          const index = buildTokenIndex(tokens, showTrim);
          populateFromTokens(index, selection);

          scheduleBrandRefresh(false);
          enhanceEmptyState();
          ensureFitsLines();
        });
        obs.observe(gridContainer, { childList: true, subtree: true });
      }

      enhanceEmptyState();
      ensureFitsLines();

      initInstrument(container);
      // Only renders where the section put a line to fill, which today means
      // the fastener surfaces. Every other collection page has no element and
      // this is a no-op.
      updateVehicleSpecLine(container);

      // Chassis mode: the collection already answers make (and usually
      // model), so a single scoped year select upgrades the shopper to an
      // exact vehicle and drives the hidden YMM machinery in one apply.
      const chassisMake = container.getAttribute('data-chassis-make') || '';
      const chassisModel = container.getAttribute('data-chassis-model') || '';
      const chassisYearEl = container.querySelector('.hrllc-grid-v1-chassis-year');
      const chassisModelEl = container.querySelector('.hrllc-grid-v1-chassis-model');
      const chassisModelField = container.querySelector('.saikospeed-chassis-model-field');

      if (container.getAttribute('data-chassis-mode') === 'true' && chassisMake && chassisYearEl) {
        const populateForTarget = function (year, model) {
          const tokens = getAvailableFitmentValues(container, fitmentParam);
          const index = buildTokenIndex(tokens, showTrim);
          populateFromTokens(index, {
            year: year, make: chassisMake, model: model || '', trim: '', fitment_code: ''
          });
        };

        const chassisFamily = container.getAttribute('data-chassis-model-family') || '';
        const syncChassisModelOptions = function () {
          if (!chassisModelEl) return;
          while (chassisModelEl.firstChild) chassisModelEl.removeChild(chassisModelEl.firstChild);
          const placeholder = document.createElement('option');
          placeholder.value = '';
          placeholder.textContent = 'Model';
          chassisModelEl.appendChild(placeholder);
          Array.prototype.forEach.call(modelEl.options, function (option) {
            if (!option.value) return;
            // The chassis family scopes the list: a Civic generation
            // offers Civic, Si, and Type R, not every cross-fit model.
            if (chassisFamily && option.value.toLowerCase().indexOf(chassisFamily.toLowerCase()) === -1) return;
            const copy = document.createElement('option');
            copy.value = option.value;
            copy.textContent = option.value;
            chassisModelEl.appendChild(copy);
          });
        };

        const applyChassis = function (year, model) {
          populateForTarget(year, model);
          yearEl.value = year;
          makeEl.value = chassisMake;
          if (model) modelEl.value = model;
          onSelectionChanged(true);
        };

        chassisYearEl.addEventListener('change', function () {
          const year = chassisYearEl.value;
          if (!year) {
            if (chassisModelField) chassisModelField.hidden = true;
            return;
          }
          if (chassisModel) {
            applyChassis(year, chassisModel);
            return;
          }
          // Multi-model chassis: surface the model list for this year.
          populateForTarget(year, '');
          syncChassisModelOptions();
          if (chassisModelEl) chassisModelEl.disabled = false;
          if (chassisModelField) chassisModelField.hidden = false;
          container.dispatchEvent(new CustomEvent('saikospeed:instrument:sync'));
        });

        if (chassisModelEl) {
          chassisModelEl.addEventListener('change', function () {
            if (!chassisYearEl.value || !chassisModelEl.value) return;
            applyChassis(chassisYearEl.value, chassisModelEl.value);
          });
        }

        // Prefill from an already-saved exact vehicle on this chassis;
        // a saved vehicle from a different model stays out of it.
        const saved = loadSelection(container);
        const savedMatchesChassis = saved.make === chassisMake
          && (!chassisModel || saved.model === chassisModel);
        if (saved.year && savedMatchesChassis) {
          chassisYearEl.value = saved.year;
          if (!chassisModel && saved.model && chassisModelEl && chassisModelField) {
            populateForTarget(saved.year, saved.model);
            syncChassisModelOptions();
            chassisModelEl.disabled = false;
            chassisModelEl.value = saved.model;
            chassisModelField.hidden = false;
          }
          container.dispatchEvent(new CustomEvent('saikospeed:instrument:sync'));
        }

        // Clearing the vehicle resets the chassis controls too.
        if (clearBtn) {
          clearBtn.addEventListener('click', function () {
            chassisYearEl.value = '';
            if (chassisModelEl) chassisModelEl.value = '';
            if (chassisModelField) chassisModelField.hidden = true;
            container.dispatchEvent(new CustomEvent('saikospeed:instrument:sync'));
          });
        }
      }
    }

    // The tool band sticks at top: var(--header-height, 6.4rem), but the
    // theme never defines that variable and the pinned site header is not
    // a constant 64px: its nav wraps to a second row between 1024 and
    // 1279px (127px tall) and runs 65/68px at mobile and small-tablet
    // widths. Measure the real header section so the stuck band always
    // lands flush beneath it instead of sliding underneath the nav.
    function syncStickyHeaderOffset() {
      const groups = document.querySelectorAll('.shopify-section-group-header-group');
      if (!groups.length) return;
      let header = null;
      groups.forEach(function (section) {
        if (section.id && section.id.indexOf('__header') !== -1) header = section;
      });
      if (!header) header = groups[groups.length - 1];
      const height = Math.round(header.getBoundingClientRect().height);
      if (height > 0) document.documentElement.style.setProperty('--header-height', height + 'px');
    }

    function init() {
      bindPerPageDropdown(document);

      syncStickyHeaderOffset();
      window.addEventListener('load', syncStickyHeaderOffset);
      let headerOffsetTimer = null;
      window.addEventListener('resize', function () {
        clearTimeout(headerOffsetTimer);
        headerOffsetTimer = setTimeout(syncStickyHeaderOffset, 150);
      });

      const containers = document.querySelectorAll('.hrllc-grid-v1-vehicle-selector');
      containers.forEach((c) => initOne(c));

      // The chassis band's Filters disclosure and instrument panels close
      // on outside click and Escape, like any well-behaved popover. An
      // open bottom sheet owns its disclosure (forced open as a section)
      // and swallows Escape as its own cancel.
      document.addEventListener('click', function (event) {
        document.querySelectorAll('details[data-saikospeed-filters][open]').forEach(function (details) {
          if (details.closest('.is-mobile-expanded')) return;
          if (!details.contains(event.target)) details.removeAttribute('open');
        });
        if (!event.target.closest('[data-instrument-seg]')) closeInstrumentPanels();
      });
      document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        const openSheetContainer = document.querySelector('.hrllc-grid-v1-vehicle-selector.is-mobile-expanded');
        if (openSheetContainer) {
          openSheetContainer.dispatchEvent(new CustomEvent('saikospeed:sheet:close'));
          return;
        }
        document.querySelectorAll('details[data-saikospeed-filters][open]').forEach(function (details) {
          details.removeAttribute('open');
        });
        closeInstrumentPanels();
      });

      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.sectionRefreshed, function () {
            // facets.js re-renders ProductGridContainer and may replace our select/input nodes
            bindPerPageDropdown(document);
          });
        }
      } catch (e) {}
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();
